import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { ArrowLeft, TrendingUp, Users, DollarSign, Info, ExternalLink, Phone, Mail, ChevronDown, ChevronUp, X, AlertCircle, CheckCircle, Clock } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/cards";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Initialize Supabase client (guarded so we can surface missing envs)
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = (supabaseUrl && supabaseKey)
  ? createClient(supabaseUrl, supabaseKey)
  : null;

// KYC Status Modal Component
const KYCStatusModal = ({ isOpen, onClose, kycStatus, onNavigateToProfile }) => {
  if (!isOpen) return null;

  const getStatusConfig = () => {
    switch (kycStatus) {
      case 'not_completed':
        return {
          icon: <AlertCircle className="h-12 w-12 text-amber-500" />,
          title: "Complete KYC to Start Investing",
          message: "Please complete your KYC verification to access our investment strategies.",
          buttonText: "Complete KYC",
          buttonClass: "bg-amber-500 hover:bg-amber-600",
          action: () => { onNavigateToProfile(); onClose(); }
        };
      case 'under_review':
        return {
          icon: <Clock className="h-12 w-12 text-blue-500" />,
          title: "KYC Under Review",
          message: "Your KYC is currently under review. Once approved, you can begin investing.",
          buttonText: "Check Status",
          buttonClass: "bg-blue-500 hover:bg-blue-600",
          action: () => { onNavigateToProfile(); onClose(); }
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#151515] border border-white/10 rounded-xl p-6 lg:p-8 max-w-md w-full">
        <div className="flex justify-between items-start mb-6">
          <div className="text-center flex-1">
            <div className="flex justify-center mb-4">{config.icon}</div>
            <h3 className="text-xl font-semibold text-white mb-2" style={{fontFamily: 'Lexend, sans-serif'}}>{config.title}</h3>
            <p className="text-gray-300 text-sm">{config.message}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 border border-white/20 text-gray-300 hover:bg-white/5 font-medium py-3 px-4 rounded-lg transition-colors"
            style={{fontFamily: 'Lexend, sans-serif'}}
          >
            Cancel
          </button>
          <button
            onClick={config.action}
            className={`flex-1 text-white font-medium py-3 px-4 rounded-lg transition-colors ${config.buttonClass}`}
            style={{fontFamily: 'Lexend, sans-serif'}}
          >
            {config.buttonText}
          </button>
          
        </div>
      </div>
      
    </div>
    
  );
};

// Investment Modal Component
const InvestmentModal = ({ isOpen, onClose, strategy, userStatus, client }) => {
  const [isSubmitting, setIsSubmitting] = useState(false); // Add this state
  const [formData, setFormData] = useState({
    selectedStrategy: strategy?.id || '',
    amount: '',
    riskAppetite: 'medium',
    comments: ''
  });

useEffect(() => {
  if (strategy) {
    const strategyId = Array.isArray(strategy) ? strategy[0]?.id : strategy.id;
    setFormData(prev => ({ ...prev, selectedStrategy: strategyId }));
  }
}, [strategy]);

const handleSubmit = async (e) => {
  e.preventDefault();
  
  try {
    setIsSubmitting(true); // Add loading state
    
    // Get user data - you'll need to pass this from parent component
    const userData = {
      client_id: userStatus?.id || client?.id, // Adjust based on your user structure
      client_name: userStatus?.name || client?.Name,
      client_email: userStatus?.email || client?.email,
      client_phone: userStatus?.phone || client?.phone
    };

const strategiesArray = Array.isArray(strategy) ? strategy : [strategy];
const selectedStrategyIds = strategiesArray.map(s => s.id || s.strategyId);
const strategyNames = strategiesArray.map(s => s.name);

const investmentData = {
  ...userData,
  selected_strategies: selectedStrategyIds,
  strategy_names: strategyNames,
  investment_amount: parseFloat(formData.amount),
  risk_appetite: formData.riskAppetite,
  comments: formData.comments,
  status: 'pending'
};

    // Insert into Supabase
    const { data, error } = await supabase
      .from('investment_requests')
      .insert([investmentData])
      .select();

    if (error) {
      throw error;
    }

    // Show success message
    // Show success notification
if (window.showNotification) {
  const strategyText = strategyNames.length > 1 
    ? `${strategyNames.length} strategies (${strategyNames.join(', ')})`
    : strategyNames[0];
    
  window.showNotification({
    type: 'success',
    title: 'Investment Request Submitted!',
    message: `Your request for ${strategyText} has been submitted successfully. We will contact you shortly.`
  });
}
    
    // Reset form and close modal
    setFormData({
      selectedStrategy: strategy?.id || '',
      amount: '',
      riskAppetite: 'medium',
      comments: ''
    });
    onClose();

  } catch (error) {
    console.error('Error submitting investment request:', error);
    if (window.showNotification) {
  window.showNotification({
    type: 'error',
    title: 'Submission Failed',
    message: 'Failed to submit investment request. Please try again.'
  });
}
  } finally {
    setIsSubmitting(false);
  }
};

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#151515] border border-white/10 rounded-xl p-6 lg:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-6">
          <h3 className="text-xl font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>Start Your Investment</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
<div>
  <label className="block text-sm font-medium text-gray-300 mb-2">
    Selected Strateg{Array.isArray(strategy) && strategy.length > 1 ? 'ies' : 'y'}
  </label>
  <div className="bg-white/5 border border-white/10 rounded-lg p-3">
    {Array.isArray(strategy) ? (
      <div className="space-y-2">
        {strategy.map((s, index) => (
          <div key={index} className="flex items-center justify-between">
            <span className="text-white font-medium">{s.name}</span>
            <span className="text-gray-400 text-sm">{s.category}</span>
          </div>
        ))}
      </div>
    ) : (
      <div className="flex items-center justify-between">
        <span className="text-white font-medium">{strategy?.name}</span>
        <span className="text-gray-400 text-sm">{strategy?.category}</span>
      </div>
    )}
  </div>
</div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Investment Amount (USD)</label>
            <input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:border-[#676DFF] focus:outline-none"
              placeholder="Enter amount"
              min="100"
              required
            />
            <p className="text-gray-400 text-xs mt-1">
              Minimum: ${strategy?.category === 'Vault' ? '1,000' : strategy?.category === 'Portfolio' ? '5,000' : '100'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Risk Appetite</label>
            <div className="grid grid-cols-3 gap-2">
              {['low', 'medium', 'high'].map((risk) => (
                <button
                  key={risk}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, riskAppetite: risk }))}
                  className={`p-2 rounded-lg border transition-colors text-sm font-medium ${
                    formData.riskAppetite === risk
                      ? 'bg-[#676DFF] border-[#676DFF] text-white'
                      : 'border-white/20 text-gray-300 hover:bg-white/5'
                  }`}
                >
                  {risk.charAt(0).toUpperCase() + risk.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Additional Comments</label>
            <textarea
              value={formData.comments}
              onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:border-[#676DFF] focus:outline-none h-20 resize-none"
              placeholder="Any specific requirements or questions..."
            />
          </div>

        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 border border-white/20 text-gray-300 hover:bg-white/5 font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-[#676DFF] hover:bg-[#5A60E8] disabled:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Investment'}
          </button>
        </div>
        </form>
      </div>
    </div>
  );
};

// Strategy Selection Modal Component
const StrategySelectionModal = ({ isOpen, onClose, strategies, clickedStrategy, onProceedToInvestment }) => {
  const [selectedStrategies, setSelectedStrategies] = useState([]);

  useEffect(() => {
    if (isOpen && clickedStrategy) {
      setSelectedStrategies([clickedStrategy.id]);
    } else if (!isOpen) {
      setSelectedStrategies([]);
    }
  }, [isOpen, clickedStrategy]);

  const handleStrategyToggle = (strategyId) => {
    setSelectedStrategies(prev => 
      prev.includes(strategyId) 
        ? prev.filter(id => id !== strategyId)
        : [...prev, strategyId]
    );
  };

  const handleProceed = () => {
    if (selectedStrategies.length > 0) {
      const selectedStrategyObjects = strategies.filter(s => selectedStrategies.includes(s.id));
      onProceedToInvestment(selectedStrategyObjects);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#151515] border border-white/10 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
              Select Investment Strategies
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-gray-400 mt-2">Choose one or more strategies to invest in:</p>
        </div>
        
        <div className="p-6 max-h-96 overflow-y-auto">
          <div className="space-y-4">
            {strategies.filter(s => s.status !== 'Closed').map((strategy) => (
              <div 
                key={strategy.id}
                className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                  selectedStrategies.includes(strategy.id) 
                    ? 'border-[#676DFF] bg-[#676DFF]/5' 
                    : 'border-white/20 hover:border-white/30'
                }`}
                onClick={() => handleStrategyToggle(strategy.id)}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      checked={selectedStrategies.includes(strategy.id)}
                      onChange={() => handleStrategyToggle(strategy.id)}
                      className="w-4 h-4 text-[#676DFF] border-gray-300 rounded focus:ring-[#676DFF]"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-medium text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {strategy.name}
                        </h3>
                        <p className="text-sm text-gray-400 mt-1">{strategy.briefSummary}</p>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-sm font-medium text-gray-400">Category</div>
                        <div className="text-lg font-semibold text-emerald-400">{strategy.category}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 border-t border-white/10">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-400">
              {selectedStrategies.length} strategy(ies) selected
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-400 border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
                style={{fontFamily: 'Lexend, sans-serif'}}
              >
                Cancel
              </button>
              <button
                onClick={handleProceed}
                disabled={selectedStrategies.length === 0}
                className="px-6 py-2 bg-[#676DFF] hover:bg-[#5A60E8] disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                style={{fontFamily: 'Lexend, sans-serif'}}
              >
                Proceed to Investment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const getDefaultHexCode = (assetId) => {
  const cryptoColors = {
    'BTC': '#F7931A',
    'ETH': '#627EEA',
    'USDC': '#2775CA',
    'USDT': '#26A17B',
    'BNB': '#F3BA2F',
    'ADA': '#0033AD',
    'SOL': '#9945FF',
    'XRP': '#23292F',
    'DOT': '#E6007A',
    'DOGE': '#C2A633',
    'AVAX': '#E84142',
    'SHIB': '#FFA409',
    'MATIC': '#8247E5',
    'LTC': '#BFBBBB',
    'ATOM': '#2E3148',
    'LINK': '#375BD2',
    'UNI': '#FF007A',
    'ALGO': '#000000',
    'VET': '#15BDFF',
    'ICP': '#29ABE2',
    'FIL': '#0090FF',
    'TRX': '#FF060A',
    'ETC': '#328332',
    'XLM': '#000000',
    'THETA': '#2AB8E6',
    'FTM': '#1969FF',
    'HBAR': '#000000',
    'MANA': '#FF2D55',
    'SAND': '#00D4FF',
    'AXS': '#0055D4',
    'FLOW': '#00EF8B',
    'NEAR': '#000000',
    'APE': '#0957D0',
    'CHZ': '#CD212A',
    'GALA': '#000000',
    'ENJ': '#624DBF'
  };
  
  return cryptoColors[assetId?.toUpperCase()] || '#676DFF';
};

const StrategiesSection = ({ 
  userStatus, 
  kycStatus,
  onNavigateToProfile,
  onStrategySelect,
  currentView,
  currentStrategy,
  client
}) => {

  const isSupabaseConfigured = Boolean(supabase);

  // Smoothly scroll the viewport (and main overflow container) to the top
  const scrollToTopSmooth = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const mainContent = document.querySelector('main .overflow-auto');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [sipAmount, setSipAmount] = useState(1000);
  const [sipDuration, setSipDuration] = useState(12);
  const [sipFAQOpen, setSipFAQOpen] = useState(null);
  
  // Modal states
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [showInvestmentModal, setShowInvestmentModal] = useState(false);
  const [selectedStrategyForInvestment, setSelectedStrategyForInvestment] = useState(null);
  
  // Supabase data states
  const [strategyData, setStrategyData] = useState({});
  const [navHistory, setNavHistory] = useState({});
  const [assetAllocations, setAssetAllocations] = useState({});
  const [sectorBreakdown, setSectorBreakdown] = useState({});
  const [sipStats, setSipStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentKycStatus, setCurrentKycStatus] = useState('not_completed');

  const [showStrategySelectionModal, setShowStrategySelectionModal] = useState(false);
  const [clickedStrategy, setClickedStrategy] = useState(null);
  const [selectedStrategiesForInvestment, setSelectedStrategiesForInvestment] = useState([]);

  // Update selectedStrategy when currentStrategy changes
  useEffect(() => {
    if (currentStrategy) {
      setSelectedStrategy(currentStrategy);
    } else if (currentView === 'list') {
      setSelectedStrategy(null);
    }
  }, [currentStrategy, currentView]);

  useEffect(() => {
    setTimeout(scrollToTopSmooth, 100);
  }, [currentView, currentStrategy]);

  // Ensure we start at the top when the public strategies page mounts
  useEffect(() => {
    scrollToTopSmooth();
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setError('Missing Supabase configuration. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in the deployment environment and redeploy.');
      setLoading(false);
      return;
    }
    fetchStrategyData();
  }, [isSupabaseConfigured]);

  const fetchStrategyData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!supabase) {
        throw new Error('Supabase client not initialized. Check environment variables.');
      }
      
      const { data: strategiesData, error: strategiesError } = await supabase
        .from('idash_investment_strategies')
        .select('*');
      
      if (strategiesError) throw strategiesError;

      const { data: navData, error: navError } = await supabase
        .from('idash_nav_history')
        .select('*');
      
      if (navError) throw navError;

      const { data: assetData, error: assetError } = await supabase
        .from('idash_strategy_asset_segregation')
        .select('*');
      
      if (assetError) throw assetError;

      const { data: sectorData, error: sectorError } = await supabase
        .from('idash_strategy_sector_segregation')
        .select('*');
      
      if (sectorError) throw sectorError;

      const { data: sipData, error: sipError } = await supabase
        .from('idash_sip_plan_card')
        .select('*');
      
      if (sipError) throw sipError;

      processStrategyData(strategiesData, navData, assetData, sectorData, sipData);
      
    } catch (err) {
      console.error('Error fetching strategy data:', err);
      setError(err.message || 'Failed to load strategies. Check configuration.');
    } finally {
      setLoading(false);
    }
  };

  // If Supabase is not configured in this build, show a clear message instead of a blank screen
  if (!isSupabaseConfigured) {
    console.error('Supabase configuration is missing in this build', { supabaseUrl, supabaseKeyPresent: !!supabaseKey });
    return (
      <div className="bg-[#0A0A0A] min-h-screen text-white flex items-center justify-center px-4" style={{fontFamily: 'Lexend, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'}}>
        <div className="max-w-xl text-center space-y-3">
          <div className="text-lg font-semibold">Missing Supabase configuration</div>
          <div className="text-gray-400 text-sm">
            Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in the deployment environment and redeploy.
          </div>
        </div>
      </div>
    );
  }

// Add this function near the top with other helper functions
const fetchCurrentNAV = async (strategyId) => {
  try {
    const { data, error } = await supabase
      .rpc('calculate_current_nav', { strategy_id: strategyId });
    
    if (error) {
      console.error(`Error fetching current NAV for ${strategyId}:`, error);
      return null;
    }
    
    return data ? parseFloat(data).toFixed(4) : null;
  } catch (err) {
    console.error(`Error calling calculate_current_nav for ${strategyId}:`, err);
    return null;
  }
};

const calculateReturnSinceInception = (navData) => {
  if (!navData || Object.keys(navData).length < 2) {
    return '0.0';
  }

  const sortedEntries = Object.entries(navData)
    .map(([date, nav]) => {
      const parsedDate = new Date(date);
      const parsedNav = parseFloat(nav);
      return { date: parsedDate, nav: parsedNav };
    })
    .filter(entry => !isNaN(entry.date.getTime()) && !isNaN(entry.nav))
    .sort((a, b) => a.date - b.date);

  if (sortedEntries.length < 2) {
    return '0.0';
  }

  const earliest = sortedEntries[0];
  const latest = sortedEntries[sortedEntries.length - 1];

  if (earliest.nav === 0) {
    return '0.0';
  }

  const totalReturnPct = ((latest.nav - earliest.nav) / earliest.nav) * 100;
  return Math.max(0, totalReturnPct).toFixed(1);
};

  const getStrategyImage = (strategyId) => {
    const imageMap = {
      'nexus-btc': '/images/bitcoin-3d.png',
      'nexus-eth': '/images/ethereum-3d.png', 
      'nexus-usd': '/images/usd-3d.png',
      'nexus-core': '/images/core-portfolio.png',
      'nexus-nova': '/images/nova-portfolio.png',
      'nexus-alpha': '/images/alpha-portfolio.png',
      'sip-service': '/images/sip-service.png'
    };
    return imageMap[strategyId] || null;
  };

  const calculateEnhancedMonthlyPerformance = (navData, strategyId) => {
    if (!navData || Object.keys(navData).length === 0) return [];
    
    const sortedEntries = Object.entries(navData)
      .map(([date, nav]) => ({ date: new Date(date), nav: parseFloat(nav) }))
      .sort((a, b) => b.date - a.date);
    
    const monthlyData = new Map();
    
    sortedEntries.forEach((entry) => {
      const monthKey = `${entry.date.getFullYear()}-${String(entry.date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData.has(monthKey)) {
        monthlyData.set(monthKey, []);
      }
      monthlyData.get(monthKey).push(entry);
    });
    
    const performance = [];
    const monthEntries = Array.from(monthlyData.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .slice(0, 24);
    
    monthEntries.forEach(([monthKey, entries]) => {
      const sortedMonthEntries = entries.sort((a, b) => a.date - b.date);
      const monthStart = sortedMonthEntries[0];
      const monthEnd = sortedMonthEntries[sortedMonthEntries.length - 1];
      
      const firstWeekEntries = sortedMonthEntries.filter(entry => {
        const dayOfMonth = entry.date.getDate();
        return dayOfMonth <= 7;
      });
      
      const firstWeekNav = firstWeekEntries.length > 0 
        ? firstWeekEntries[firstWeekEntries.length - 1].nav
        : monthStart.nav;
      
      const monthlyReturn = monthStart.nav !== monthEnd.nav 
        ? ((monthEnd.nav - monthStart.nav) / monthStart.nav * 100)
        : 0;
      
      let sinceInceptionAPY = 0;
      const earliestEntry = sortedEntries[sortedEntries.length - 1];
      
      if (earliestEntry && earliestEntry.nav !== monthEnd.nav && earliestEntry.date < monthEnd.date) {
        const actualDays = (monthEnd.date - earliestEntry.date) / (1000 * 60 * 60 * 24);
        
        if (actualDays >= 30) {
          const totalReturn = (monthEnd.nav - earliestEntry.nav) / earliestEntry.nav;
          sinceInceptionAPY = (Math.pow(1 + totalReturn, 365.25 / actualDays) - 1) * 100;
        }
      }
      
      performance.push({
        month: monthStart.date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        firstWeekNAV: firstWeekNav.toFixed(4),
        monthlyReturn: monthlyReturn.toFixed(2),
        trailingAPY: sinceInceptionAPY.toFixed(1),
      });
    });
    
    return performance;
  };


const processStrategyData = async (strategiesData, navData, assetData, sectorData, sipData) => {
  const strategyMap = {};
  strategiesData.forEach(strategy => {
    strategyMap[strategy.strategy_id] = {
      ...strategy,
      apy: strategy.apy_since_inception ? (parseFloat(strategy.apy_since_inception) * 100).toFixed(1) + '%' : '0%',
      totalInvestors: strategy.total_investors || 0
    };
  });

  const navMap = {};
  navData.forEach(nav => {
    if (nav.nav_history && typeof nav.nav_history === 'object') {
      navMap[nav.strategy_id] = nav.nav_history;
    }
  });

  const assetMap = {};
  assetData.forEach(asset => {
    if (!assetMap[asset.strategy_id]) {
      assetMap[asset.strategy_id] = [];
    }
    if (parseFloat(asset.allocation) > 0) {
      assetMap[asset.strategy_id].push(asset);
    }
  });

  const sectorMap = {};
  sectorData.forEach(sector => {
    if (!sectorMap[sector.strategy_id]) {
      sectorMap[sector.strategy_id] = {};
    }
    if (sector.sector_id && sector.sector_name) {
      if (!sectorMap[sector.strategy_id][sector.sector_id]) {
        sectorMap[sector.strategy_id][sector.sector_id] = {
          sector_name: sector.sector_name,
          sector_description: sector.sector_description || '',
          allocation: 0
        };
      }
      sectorMap[sector.strategy_id][sector.sector_id].allocation += parseFloat(sector.allocation || 0);
    }
  });

  const sipStatsData = {
    totalPlans: sipData.filter(plan => plan.status).length,
    activePlans: sipData.filter(plan => plan.status).length,
    totalInvested: sipData.reduce((sum, plan) => sum + parseFloat(plan.total_invested || 0), 0)
  };

  // Helper function to get current NAV using the calculate_current_nav function
const getCurrentNAVFromMap = async (strategyId) => {
  try {
    // First try to get current NAV from the function
    const { data, error } = await supabase
      .rpc('calculate_current_nav', { p_portfolio_id: strategyId });
    
    if (!error && data !== null) {
      return parseFloat(data).toFixed(4);
    } else {
      console.warn(`⚠️ calculate_current_nav failed for ${strategyId}:`, error);
    }
  } catch (err) {
    console.error(`❌ Error calling calculate_current_nav for ${strategyId}:`, err);
  }
  
  // Fallback to navMap if function fails
  if (!navMap[strategyId]) return '1.0000';
  
  const navData = navMap[strategyId];
  const dates = Object.keys(navData).sort((a, b) => new Date(b) - new Date(a));
  
  if (dates.length > 0) {
    const latestNAV = navData[dates[0]];
    return parseFloat(latestNAV).toFixed(4);
  }
  
  return '1.0000';
};

  // Get all NAV values concurrently
  const [btcvNav, ethvNav, stablecNav, bluecNav, altcNav, smallcNav] = await Promise.all([
    getCurrentNAVFromMap('BTCV'),
    getCurrentNAVFromMap('ETHV'),
    getCurrentNAVFromMap('STABLEC'),
    getCurrentNAVFromMap('BLUEC'),
    getCurrentNAVFromMap('ALTC'),
    getCurrentNAVFromMap('SMALLC')
  ]);

  // Create updated strategies using the fetched NAV values
  const updatedStrategies = [
    {
      id: 'nexus-btc',
      category: 'Vault',
      name: 'Nexus BTC',
      briefSummary: 'Build long-term Bitcoin exposure with enhanced yield.',
      apy: navMap['BTCV'] ? `${calculateReturnSinceInception(navMap['BTCV'])}%` : '3.1%',
      nav: btcvNav,
      riskLevel: 'Medium',
      status: 'Live',
      strategyId: 'BTCV'
    },
    {
      id: 'nexus-eth',
      category: 'Vault',
      name: 'Nexus ETH',
      briefSummary: 'Capture Ethereum\'s upside while compounding yield.',
      apy: navMap['ETHV'] ? `${calculateReturnSinceInception(navMap['ETHV'])}%` : '5.0%',
      nav: ethvNav,
      riskLevel: 'Medium',
      status: 'Live',
      strategyId: 'ETHV'
    },
    {
      id: 'nexus-usd',
      category: 'Vault',
      name: 'Nexus USD',
      briefSummary: 'Stable, consistent yield from USD-pegged assets.',
      apy: navMap['STABLEC'] ? `${calculateReturnSinceInception(navMap['STABLEC'])}%` : '9.1%',
      nav: stablecNav,
      riskLevel: 'Low',
      status: 'Live',
      strategyId: 'STABLEC'
    },
    {
      id: 'nexus-core',
      category: 'Portfolio',
      name: 'Nexus Core',
      briefSummary: 'The foundation of digital asset investing.',
      nav: bluecNav,
      investors: strategyMap['BLUEC']?.totalInvestors || 24,
      riskLevel: 'Medium',
      status: 'Open',
      strategyId: 'BLUEC'
    },
    {
      id: 'nexus-nova',
      category: 'Portfolio',
      name: 'Nexus Nova',
      briefSummary: 'Positioned for the next era of blockchain adoption.',
      nav: altcNav,
      investors: strategyMap['ALTC']?.totalInvestors || 31,
      riskLevel: 'Medium',
      status: 'Open',
      strategyId: 'ALTC'
    },
    {
      id: 'nexus-alpha',
      category: 'Portfolio',
      name: 'Nexus Alpha',
      briefSummary: 'Harnessing early-stage innovation with active management.',
      nav: smallcNav,
      investors: strategyMap['SMALLC']?.totalInvestors || 27,
      riskLevel: 'High',
      status: 'Open',
      strategyId: 'SMALLC'
    },
    {
      id: 'sip-service',
      category: 'SIP',
      name: 'Systematic Investment Plan',
      briefSummary: 'Automated recurring investments across our portfolio strategies with dollar-cost averaging.',
      minInvestment: '$50',
      assets: 'BTC, ETH +',
      totalPlans: sipStatsData.activePlans?.toString() || '17',
      riskLevel: 'Variable',
      status: 'Open'
    }
  ];

  // Update all states
  setStrategyData(strategyMap);
  setNavHistory(navMap);
  setAssetAllocations(assetMap);
  setSectorBreakdown(sectorMap);
  setSipStats(sipStatsData);
  setStrategies(updatedStrategies);
};



  const getCurrentNAV = (strategyId) => {
    if (!navHistory[strategyId]) return '1.0000';
    
    const navData = navHistory[strategyId];
    const dates = Object.keys(navData).sort((a, b) => new Date(b) - new Date(a));
    
    if (dates.length > 0) {
      const latestNAV = navData[dates[0]];
      return parseFloat(latestNAV).toFixed(4);
    }
    
    return '1.0000';
  };

const handleInvestNow = (strategy) => {
  const actualKycStatus = userStatus?.kycStatus || userStatus?.status || 'not_completed';

  // Map the status values correctly
  let mappedKycStatus;
  if (actualKycStatus === 'verified' || actualKycStatus === 'approved') {
    mappedKycStatus = 'approved';
  } else if (actualKycStatus === 'pending' || actualKycStatus === 'under_review' || actualKycStatus === 'submitted') {
    mappedKycStatus = 'under_review';
  } else {
    mappedKycStatus = 'not_completed';
  }

  if (mappedKycStatus === 'approved') {
    // Show strategy selection modal instead of going directly to investment
    setClickedStrategy(strategy);
    setShowStrategySelectionModal(true);
  } else {
    // Store the mapped status for the KYC modal
    setCurrentKycStatus(mappedKycStatus);
    setShowKYCModal(true);
  }
};

  const handleStrategySelect = (strategy) => {
    setSelectedStrategy(strategy);
    if (onStrategySelect) {
      onStrategySelect(strategy);
    }
  };

  const handleBackToStrategies = () => {
    setSelectedStrategy(null);
    if (onStrategySelect) {
      onStrategySelect(null);
    }
  };

const handleContactAdvisor = () => {
  const actualKycStatus = userStatus?.kycStatus || userStatus?.status || 'not_completed';
  
  let mappedKycStatus;
  if (actualKycStatus === 'verified' || actualKycStatus === 'approved') {
    mappedKycStatus = 'approved';
  } else if (actualKycStatus === 'pending' || actualKycStatus === 'under_review' || actualKycStatus === 'submitted') {
    mappedKycStatus = 'under_review';
  } else {
    mappedKycStatus = 'not_completed';
  }
  
  if (mappedKycStatus === 'approved') {
    // If on detailed view, use the current strategy as clicked strategy
    if (selectedStrategy) {
      setClickedStrategy(selectedStrategy);
    }
    setShowStrategySelectionModal(true);
  } else {
    setCurrentKycStatus(mappedKycStatus);
    setShowKYCModal(true);
  }
};

const handleProceedToInvestment = (selectedStrategies) => {
  setSelectedStrategiesForInvestment(selectedStrategies);
  setShowStrategySelectionModal(false);
  setShowInvestmentModal(true);
};

  const faqData = [
    {
      question: "What are the minimum investment amounts?",
      answer: "Our minimum investment amounts for Vaults and Portfolios start from $1,000 and SIP plans from $50 per month."
    },
    {
      question: "How often can I withdraw my investments?",
      answer: "There are no lock-in periods. You can request withdrawals anytime from your dashboard. All withdrawals are processed within 7 working days."
    },
    {
      question: "What fees do you charge?",
      answer: "We charge three types of fees: a Performance Fee on returns earned, a Management Fee on assets under management, and a Swap Fee per SIP transaction. Exact rates are available on each Vault's details page."
    },
    {
      question: "How is my investment protected?",
      answer: "We use air-gapped cold storage, diversify across multiple wallets, publish periodic proof-of-reserves reports, and maintain 100% on-chain investments following the highest crypto security standards."
    },
    {
      question: "How do I track my investment performance?",
      answer: "All your holdings, returns, and activity are visible on your dashboard in real-time."
    }
  ];

  const formatCurrency = (value, decimals = 2) => {
    if (!value || isNaN(value)) return '0.00';
    return parseFloat(value).toFixed(decimals);
  };

  // Strategy Card Component
  const StrategyCard = ({ strategy }) => {
    if (!strategy) return null;

    const strategyDetails = strategyData[strategy.strategyId] || {};
    const currentNAV = getCurrentNAV(strategy.strategyId) || strategy.nav;
    const strategyImage = getStrategyImage(strategy.id);
    return (
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-7 hover:bg-white/10 hover:border-[#676DFF]/30 transition-all duration-300 group h-full flex flex-col min-h-[380px] sm:min-h-[420px]" style={{fontFamily: 'Lexend, sans-serif'}}>
        
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-3 min-w-0 flex-1">
            {strategyImage ? (
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg overflow-hidden shadow-lg flex-shrink-0">
                <img 
                  src={strategyImage}
                  alt={strategy.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.parentNode.innerHTML = `
                      <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-[#676DFF] to-[#5A60E8] flex items-center justify-center text-white font-semibold text-xs sm:text-sm shadow-lg">
                        ${strategy.category === 'Vault' ? 'V' : strategy.category === 'Portfolio' ? 'P' : 'S'}
                      </div>
                    `;
                  }}
                />
              </div>
            ) : (
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-[#676DFF] to-[#5A60E8] flex items-center justify-center text-white font-semibold text-xs sm:text-sm shadow-lg flex-shrink-0">
                {strategy.category === 'Vault' ? 'V' : strategy.category === 'Portfolio' ? 'P' : 'S'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="text-white font-semibold text-base sm:text-lg leading-tight break-words">{strategy.name}</h3>
            </div>
          </div>
          
        </div>

        

        {/* Description */}
        <div className="mb-4 flex-1">
          <p className="text-gray-300 text-xs sm:text-sm leading-relaxed line-clamp-3">{strategy.briefSummary}</p>
        </div>

        {/* Metrics Grid */}
        <div className="mb-4 sm:mb-5 h-16 sm:h-20 flex items-center">
          {strategy.category === 'Vault' && (
            <div className="grid grid-cols-2 gap-3 sm:gap-6 w-full">
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <DollarSign className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-[#676DFF] mr-1" />
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">NAV</p>
                </div>
                <p className="text-[#676DFF] font-bold text-lg sm:text-xl">{currentNAV}</p>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <TrendingUp className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-emerald-400 mr-1" />
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium whitespace-nowrap">{strategy.category === 'Vault' ? 'APY (All Time)' : 'APY'}</p>
                </div>
                <p className="text-emerald-400 font-bold text-lg sm:text-xl">{strategy.apy}</p>
              </div>
            </div>
          )}

          {strategy.category === 'Portfolio' && (
            <div className="grid grid-cols-2 gap-3 sm:gap-6 w-full">
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <DollarSign className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-[#676DFF] mr-1" />
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">NAV</p>
                </div>
                <p className="text-[#676DFF] font-bold text-lg sm:text-xl">{currentNAV}</p>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <Users className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-gray-400 mr-1" />
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Investors</p>
                </div>
                <p className="text-white font-bold text-lg sm:text-xl">{strategyDetails.totalInvestors || strategy.investors}</p>
              </div>
            </div>
          )}

          {strategy.category === 'SIP' && (
            <div className="grid grid-cols-3 gap-2 sm:gap-3 w-full">
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <TrendingUp className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-emerald-400 mr-1" />
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">MIN</p>
                </div>
                <p className="text-emerald-400 font-bold text-sm sm:text-lg">{strategy.minInvestment}</p>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <Info className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-amber-400 mr-1" />
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Assets</p>
                </div>
                <p className="text-amber-400 font-semibold text-xs sm:text-sm">{strategy.assets}</p>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <DollarSign className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-[#676DFF] mr-1" />
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Plans</p>
                </div>
                <p className="text-[#676DFF] font-bold text-sm sm:text-lg">{sipStats.activePlans || strategy.totalPlans}</p>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-auto">
          <button 
            onClick={() => {
              handleStrategySelect(strategy);
              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
                
                const mainContent = document.querySelector('main .overflow-auto');
                if (mainContent) {
                  mainContent.scrollTop = 0;
                  mainContent.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }, 50);
            }}
            disabled={strategy.status === 'Closed' || loading}
            className="flex-1 bg-[#676DFF] hover:bg-[#5A60E8] disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2.5 px-4 rounded-lg transition-colors duration-200 text-sm"
            style={{fontFamily: 'Lexend, sans-serif'}}
          >
            View More
          </button>
          <button 
            onClick={() => handleInvestNow(strategy)}
            className="flex-1 border border-white/20 text-gray-300 hover:bg-white/5 hover:text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-200 text-sm"
            style={{fontFamily: 'Lexend, sans-serif'}}
          >
            Invest Now
          </button>
        </div>
      </div>
    );
  };

  // NAV Chart Component
  const NAVChart = ({ data }) => {
    if (!data || Object.keys(data).length === 0) {
      return (
        <div className="h-64 sm:h-80 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center">
          <p className="text-gray-400 text-sm sm:text-base">No NAV data available</p>
        </div>
      );
    }

    const chartData = Object.entries(data)
      .map(([date, nav]) => ({
        date: date,
        displayDate: new Date(date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        nav: parseFloat(nav),
        fullDate: new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const tickInterval = Math.ceil(chartData.length / (window.innerWidth < 640 ? 6 : 10));

    return (
      <div className="h-64 sm:h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 15, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis 
              dataKey="displayDate" 
              stroke="#9CA3AF"
              tick={{ fill: '#9CA3AF', fontSize: window.innerWidth < 640 ? 10 : 11 }}
              interval={tickInterval}
              angle={-45}
              textAnchor="end"
              height={window.innerWidth < 640 ? 50 : 60}
            />
            <YAxis 
              stroke="#9CA3AF"
              tick={{ fill: '#9CA3AF', fontSize: window.innerWidth < 640 ? 10 : 12 }}
              domain={['dataMin - 0.01', 'dataMax + 0.01']}
              tickFormatter={(value) => value.toFixed(3)}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#1F2937', 
                border: '1px solid #374151',
                borderRadius: '8px',
                fontSize: window.innerWidth < 640 ? '12px' : '14px'
              }}
              labelFormatter={(value, payload) => {
                if (payload && payload[0]) {
                  return payload[0].payload.fullDate;
                }
                return value;
              }}
              formatter={(value) => [`NAV: ${value.toFixed(4)}`, '']}
            />
            <Line 
              type="monotone" 
              dataKey="nav" 
              stroke="#676DFF" 
              strokeWidth={2}
              dot={false}
              activeDot={{ r: window.innerWidth < 640 ? 4 : 6, fill: '#676DFF' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };
  // Complete DetailedStrategyView implementation with responsive design
  const DetailedStrategyView = ({ 
  strategy, 
  onInvestNow, 
  onContactAdvisor, 
  showKYCModal,
  setShowKYCModal,
  showStrategySelectionModal,
  setShowStrategySelectionModal,
  showInvestmentModal,
  setShowInvestmentModal,
  currentKycStatus,
  setCurrentKycStatus
}) => {
    const [sipAmount, setSipAmount] = useState(1000);
    const [sipDuration, setSipDuration] = useState(12);
    const [sipFAQOpen, setSipFAQOpen] = useState(null);
    const [monthlyPerformance, setMonthlyPerformance] = useState([]);
    const [strategyAssets, setStrategyAssets] = useState([]);
    const [strategySectors, setStrategySectors] = useState([]);

    useEffect(() => {
      if (strategy && strategy.strategyId) {
        fetchDetailedStrategyData(strategy.strategyId);
      }
    }, [strategy]);

    const fetchDetailedStrategyData = async (strategyId) => {
      try {
        if (navHistory[strategyId] && Object.keys(navHistory[strategyId]).length > 0) {
          const performance = calculateEnhancedMonthlyPerformance(navHistory[strategyId], strategyId);
          setMonthlyPerformance(performance);
        }

        const existingAssets = assetAllocations[strategyId] || [];

        if (existingAssets.length > 0) {
          const assetIds = existingAssets.map(asset => asset.asset_id);
          
          const { data: hexData, error: hexError } = await supabase
            .from('idash_portfolio_inner_summary_asset_breakdown')
            .select('asset_id, hex_code')
            .eq('portfolio_id', strategyId)
            .in('asset_id', assetIds);
          
          const hexLookup = {};
          if (hexData && !hexError) {
            hexData.forEach(item => {
              if (item.hex_code) {
                hexLookup[item.asset_id] = item.hex_code;
              }
            });
          }
          
          const enhancedAssets = existingAssets.map(asset => ({
            ...asset,
            hex_code: hexLookup[asset.asset_id] || null,
          }));
          
          setStrategyAssets(enhancedAssets);
        } else {
          setStrategyAssets([]);
        }

        const sectors = sectorBreakdown[strategyId] || {};
        setStrategySectors(Object.values(sectors));

      } catch (err) {
        console.error('Error fetching detailed strategy data:', err);
        setStrategyAssets([]);
        setStrategySectors([]);
      }
    };

const calculateSIPReturns = () => {
  const monthlyRate = 0.35 / 12; // Changed from 0.12 to 0.35 (35% annual return)
  const totalMonths = sipDuration;
  const futureValue = sipAmount * (((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate) * (1 + monthlyRate));
  const totalInvested = sipAmount * totalMonths;
  const returns = futureValue - totalInvested;
  
  return {
    totalInvested: totalInvested.toFixed(0),
    futureValue: futureValue.toFixed(0),
    returns: returns.toFixed(0)
  };
};

    if (!strategy) {
      return (
        <div className="text-center text-white p-6 lg:p-12" style={{fontFamily: 'Lexend, sans-serif'}}>
          <h2 className="text-xl lg:text-2xl font-semibold mb-4">Strategy not found</h2>
          <button 
            onClick={() => {
              handleBackToStrategies();
              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
                
                const mainContent = document.querySelector('main .overflow-auto');
                if (mainContent) {
                  mainContent.scrollTop = 0;
                  mainContent.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }, 50);
            }}
            className="flex items-center text-gray-400 hover:text-white transition-colors font-medium"
          >
            <ArrowLeft className="h-4 w-4 lg:h-5 lg:w-5 mr-2 lg:mr-3" />
            Back to Strategies
          </button>
        </div>
      );
    }

    const currentStrategyData = strategyData[strategy.strategyId] || {};
    const currentNAV = getCurrentNAV(strategy.strategyId) || strategy.nav;

    // VAULT DETAILED VIEW
// VAULT DETAILED VIEW
if (strategy.category === 'Vault') {
  return (
    <div className="space-y-6 lg:space-y-8" style={{fontFamily: 'Lexend, sans-serif'}}>
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button 
          onClick={handleBackToStrategies}
          className="flex items-center text-gray-400 hover:text-white transition-colors font-medium"
        >
          <ArrowLeft className="h-4 w-4 lg:h-5 lg:w-5 mr-2 lg:mr-3" />
          <span className="text-sm lg:text-base">Back to Strategies</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6 lg:space-y-8">
          {/* Portfolio Name & Summary */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start mb-6 space-y-4 lg:space-y-0">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-4">
                  {getStrategyImage(strategy.id) ? (
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl overflow-hidden shadow-lg">
                      <img 
                        src={getStrategyImage(strategy.id)}
                        alt={strategy.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.parentNode.innerHTML = `
                            <div class="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-gradient-to-br from-[#676DFF] to-[#5A60E8] flex items-center justify-center">
                              <span class="text-white font-bold text-base lg:text-lg">V</span>
                            </div>
                          `;
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-gradient-to-br from-[#676DFF] to-[#5A60E8] flex items-center justify-center">
                      <span className="text-white font-bold text-base lg:text-lg">V</span>
                    </div>
                  )}
                  <h1 className="text-xl sm:text-2xl font-bold text-white">{strategy.name}</h1>
                </div>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  {(() => {
                    switch (strategy.name) {
                      case 'Nexus BTC':
                        return 'Nexus BTC combines core BTC holding with yield-generating strategies across DeFi. By deploying into lending, liquidity provisioning, and conservative market-neutral opportunities, the vault generates sustainable returns — while keeping Bitcoin as the foundation of growth.';
                      case 'Nexus ETH':
                        return 'Nexus ETH provides direct ETH exposure alongside structured yield strategies. Through staking, lending, and DeFi-native opportunities, the vault maximizes compounding potential and long-term ETH accumulation — designed for believers in Ethereum\'s future.';
                      case 'Nexus USD':
                        return 'Nexus USD transforms stablecoins into productive assets by allocating across lending markets, fixed income protocols, and institutional-grade opportunities. Built to minimize volatility, the vault delivers reliable, predictable returns — making it an ideal core holding for steady growth.';
                      default:
                        return 'Generate sustainable yield through diversified DeFi strategies while maintaining security and liquidity.';
                    }
                  })()}
                </p>
              </div>
              <div className="flex flex-col space-y-3 lg:ml-8">
                <button 
                  onClick={() => onInvestNow(strategy)}
                  className="bg-[#676DFF] hover:bg-[#5A60E8] text-white font-medium py-3 px-4 lg:px-6 rounded-lg transition-colors text-sm lg:text-base"
                >
                  Invest Now
                </button>
              </div>
            </div>
          </div>

          {/* Mobile: Performance Metrics - Show only on mobile */}
          <div className="block lg:hidden bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Current Performance</h3>
            <div className="space-y-4">
              <div className="text-center p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <p className="text-xs text-gray-400 mb-1 font-medium whitespace-nowrap">APY (Since Inception)</p>
                <p className="text-2xl font-bold text-emerald-400">{strategy.apy}</p>
              </div>
              <div className="text-center p-4 bg-[#676DFF]/10 border border-[#676DFF]/20 rounded-lg">
                <p className="text-xs text-gray-400 mb-1 font-medium">Total NAV</p>
                <p className="text-2xl font-bold text-[#676DFF]">{currentNAV}</p>
              </div>
            </div>
          </div>

          {/* NAV Chart */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">NAV Performance</h3>
            {navHistory[strategy.strategyId] ? (
              <NAVChart data={navHistory[strategy.strategyId]} />
            ) : (
              <div className="h-64 sm:h-80 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <TrendingUp className="h-12 w-12 lg:h-16 lg:w-16 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400 text-base lg:text-lg font-medium">No NAV data available</p>
                </div>
              </div>
            )}
          </div>{/* Asset Allocation */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">Asset Allocation</h3>
            <div className="space-y-3 lg:space-y-4">
              {(() => {
                const assets = strategyAssets.filter(asset => {
                  const allocation = parseFloat(asset.allocation || 0);
                  return allocation > 0;
                }).sort((a, b) => parseFloat(b.allocation || 0) - parseFloat(a.allocation || 0));
                
                if (assets.length === 0) {
                  return (
                    <div className="text-center py-8 lg:py-12 text-gray-400">
                      <div className="w-12 h-12 lg:w-16 lg:h-16 bg-gray-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <div className="w-6 h-6 lg:w-8 lg:h-8 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                      <p className="text-sm lg:text-base">Loading asset allocation...</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3 lg:space-y-4">
                    {assets.map((asset, index) => {
                      const allocation = parseFloat(asset.allocation || 0);
                      const hexFromDB = asset.hex_code;
                      const hexFromDefault = getDefaultHexCode(asset.asset_id);
                      const color = hexFromDB || hexFromDefault;
                      
                      return (
                        <div key={`${asset.asset_id}-${index}`} className="group">
                          {/* Mobile Card Layout */}
                          <div className="block sm:hidden">
                            <div className="p-3 bg-white/5 rounded-lg border border-white/10 hover:bg-white/8 transition-colors">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2 flex-1 min-w-0">
                                  <div 
                                    className="w-3 h-3 rounded-full flex-shrink-0" 
                                    style={{ backgroundColor: color }}
                                  ></div>
                                  <span className="text-white font-medium text-sm truncate">
                                    {asset.asset_name || asset.asset_id}
                                  </span>
                                </div>
                                <span className="text-[#676DFF] font-bold text-lg ml-2 flex-shrink-0">
                                  {allocation.toFixed(1)}%
                                </span>
                              </div>
                              <div className="mt-2">
                                <div className="w-full bg-white/10 rounded-full h-2">
                                  <div 
                                    className="h-2 rounded-full transition-all duration-500 ease-out"
                                    style={{ 
                                      backgroundColor: color, 
                                      width: `${Math.min(allocation, 100)}%` 
                                    }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Desktop Row Layout */}
                          <div className="hidden sm:flex items-center justify-between p-3 lg:p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/8 transition-colors">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <div 
                                className="w-3 h-3 lg:w-4 lg:h-4 rounded-full flex-shrink-0" 
                                style={{ backgroundColor: color }}
                              ></div>
                              <div className="flex-1 min-w-0">
                                <span className="text-white font-medium text-sm lg:text-base block truncate">
                                  {asset.asset_name || asset.asset_id}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-4 flex-shrink-0">
                              <div className="w-24 lg:w-32 bg-white/10 rounded-full h-2 lg:h-3">
                                <div 
                                  className="h-2 lg:h-3 rounded-full transition-all duration-500 ease-out"
                                  style={{ 
                                    backgroundColor: color, 
                                    width: `${Math.min(allocation, 100)}%` 
                                  }}
                                ></div>
                              </div>
                              <span className="text-gray-400 text-xs lg:text-sm font-medium w-16 text-right">
                                {allocation.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Monthly Performance */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">Monthly Performance</h3>
            <div className="max-h-96 overflow-y-auto overflow-x-auto border border-white/10 rounded-lg">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 lg:py-3 px-2 lg:px-4 text-gray-400 font-medium">Month</th>
                    <th className="text-right py-2 lg:py-3 px-2 lg:px-4 text-gray-400 font-medium">NAV</th>
                    <th className="text-right py-2 lg:py-3 px-2 lg:px-4 text-gray-400 font-medium">Monthly Return</th>
                    <th className="text-right py-2 lg:py-3 px-2 lg:px-4 text-gray-400 font-medium hidden sm:table-cell">Trailing 12M APY</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {monthlyPerformance.length > 0 ? (
                    monthlyPerformance.map((month, index) => (
                      <tr key={index} className="hover:bg-white/5 transition-colors">
                        <td className="py-2 lg:py-3 px-2 lg:px-4 text-white font-medium">{month.month}</td>
                        <td className="py-2 lg:py-3 px-2 lg:px-4 text-right text-[#676DFF] font-semibold">{month.firstWeekNAV}</td>
                        <td className="py-2 lg:py-3 px-2 lg:px-4 text-right font-semibold">
                          <span className={parseFloat(month.monthlyReturn) >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                            {parseFloat(month.monthlyReturn) >= 0 ? '+' : ''}{month.monthlyReturn}%
                          </span>
                        </td>
                        <td className="py-2 lg:py-3 px-2 lg:px-4 text-right text-emerald-400 font-semibold hidden sm:table-cell">
                          {month.trailingAPY}%
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="py-6 lg:py-8 text-center text-gray-400">
                        Loading performance data...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>{/* Mobile: Fee Information - Show only on mobile */}
          <div className="block lg:hidden bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Fee Structure</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 font-medium text-sm">Management Fee</span>
                <span className="font-bold text-white">0%</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 font-medium text-sm">Performance Fee</span>
                <span className="font-bold text-white">20%</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 font-medium text-sm">Withdrawal Fee</span>
                <span className="font-bold text-emerald-400">0%</span>
              </div>
            </div>
          </div>

          {/* Mobile: Withdrawal Information - Show only on mobile */}
          <div className="block lg:hidden bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Withdrawal Policy</h3>
            <div className="space-y-3 text-xs text-gray-300">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                <p>Withdrawals processed within 3-7 days</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                <p>No lock-up periods or penalties</p>
              </div>
            </div>
          </div>

          {/* Protocols and Strategies */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
            <div className="space-y-6 lg:space-y-8">
              {/* Protocols Used */}
            <div>
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">Protocols Used</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(() => {
                    // Define protocols for each strategy
                    const protocolsByStrategy = {
                      'Nexus BTC': [
                        { name: 'Morpho', url: 'https://morpho.xyz', logo: '/images/morpho-logo.png' },
                        { name: 'Fluid', url: 'https://fluid.instadapp.io', logo: '/images/fluid-logo.png' },
                        { name: 'Aave', url: 'https://aave.com', logo: '/images/aave-logo.png' },
                        { name: 'Pendle', url: 'https://pendle.finance', logo: '/images/pendle-logo.png' }
                      ],
                      'Nexus ETH': [
                        { name: 'Maple', url: 'https://maple.finance', logo: '/images/maple-logo.png' },
                        { name: 'Morpho', url: 'https://morpho.xyz', logo: '/images/morpho-logo.png' },
                        { name: 'Fluid', url: 'https://fluid.instadapp.io', logo: '/images/fluid-logo.png' },
                        { name: 'Lido', url: 'https://lido.fi', logo: '/images/lido-logo.png' },
                        { name: 'Kelp', url: 'https://kelpdao.xyz', logo: '/images/kelp-logo.png' },
                        { name: 'EtherFi', url: 'https://ether.fi', logo: '/images/etherfi-logo.png' },
                        { name: 'Pendle', url: 'https://pendle.finance', logo: '/images/pendle-logo.png' }
                      ],
                      'Nexus USD': [
                        { name: 'Hyperliquid', url: 'https://hyperliquid.xyz', logo: '/images/hyperliquid-logo.png' },
                        { name: 'Aave', url: 'https://aave.com', logo: '/images/aave-logo.png' },
                        { name: 'Fluid', url: 'https://fluid.instadapp.io', logo: '/images/fluid-logo.png' },
                        { name: 'Syrup', url: 'https://syrup.com', logo: '/images/syrup-logo.png' },
                        { name: 'Pendle', url: 'https://pendle.finance', logo: '/images/pendle-logo.png' },
                        { name: 'Euler', url: 'https://euler.finance', logo: '/images/euler-logo.png' },
                        { name: 'Morpho', url: 'https://morpho.xyz', logo: '/images/morpho-logo.png' }
                      ]
                    };

                    // Get protocols for current strategy
                    const currentProtocols = protocolsByStrategy[strategy.name] || [];

                    return currentProtocols.map((protocol, index) => (
                      <a 
                        key={index}
                        href={protocol.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="flex items-center p-3 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-colors group"
                      >
                        <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-white/10 flex items-center justify-center">
                          <img 
                            src={protocol.logo}
                            alt={`${protocol.name} logo`}
                            className="w-6 h-6 object-contain"
                            onError={(e) => {
                              // Fallback to gradient background if logo fails to load
                              e.target.style.display = 'none';
                              e.target.parentNode.style.background = 'linear-gradient(135deg, #676DFF, #5A60E8)';
                              e.target.parentNode.innerHTML = `<span class="text-white font-semibold text-xs">${protocol.name.charAt(0)}</span>`;
                            }}
                          />
                        </div>
                        <span className="text-white font-medium ml-3 flex-1">{protocol.name}</span>
                        <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-white transition-colors" />
                      </a>
                    ));
                  })()}
                </div>
              </div>

              {/* Strategies Implemented */}
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">Strategies Implemented</h3>
                <div className="space-y-3">
                  <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                    <h4 className="text-white font-semibold mb-2">Lending/Borrowing Arbitrage</h4>
                    <p className="text-gray-400 text-sm">Capitalize on rate differentials across protocols</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                    <h4 className="text-white font-semibold mb-2">Looping</h4>
                    <p className="text-gray-400 text-sm">Reinvest borrowed assets to amplify yield potential</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                    <h4 className="text-white font-semibold mb-2">Institutional Lending</h4>
                    <p className="text-gray-400 text-sm">Lend to established institutions for better lending rates</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                    <h4 className="text-white font-semibold mb-2">Yield Farming</h4>
                    <p className="text-gray-400 text-sm">Maximize returns through protocol incentives</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                    <h4 className="text-white font-semibold mb-2">Fixed Interest</h4>
                    <p className="text-gray-400 text-sm">Fix yield rates using credit/debit swaps</p>
                  </div>
                  {strategy.name.includes('USD') && (
                    <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                      <h4 className="text-white font-semibold mb-2">Delta Neutral Strategies</h4>
                      <p className="text-gray-400 text-sm">Earn on long and short positions while maintaining delta neutrality</p>
                    </div>
                  )}
                </div>
              </div>{/* Risks */}
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">Risk Considerations</h3>
                <div className="space-y-3">
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <h4 className="text-red-400 font-semibold mb-2">Smart Contract Risk</h4>
                    <p className="text-gray-400 text-sm">Potential vulnerabilities from DeFi protocols</p>
                  </div>
                  {strategy.name.includes('USD') && (
                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <h4 className="text-red-400 font-semibold mb-2">Depeg Risk</h4>
                      <p className="text-gray-400 text-sm">Risk from stablecoin exposure losing its peg</p>
                    </div>
                  )}
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <h4 className="text-red-400 font-semibold mb-2">Liquidation Risk</h4>
                    <p className="text-gray-400 text-sm">Potential losses in borrowing strategies during market volatility</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Vault FAQ */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">Frequently Asked Questions</h3>
            <div className="space-y-3 lg:space-y-4">
              {[
                {
                  question: "How does the vault generate yield?",
                  answer: `${strategy.name} uses a mix of DeFi strategies to earn returns. You can see the exact strategy allocation and performance anytime on the Vault Info page.`
                },
                {
                  question: "What is the minimum investment amount?",
                  answer: "The minimum investment for vault strategies is $1,000. There are no maximum limits, making our vaults suitable for both individual and institutional investors."
                },
                {
                  question: "How often can I withdraw my funds?",
                  answer: "No lock-in periods. You can request withdrawals anytime via your dashboard. Withdrawals are processed within 7 working days."
                },
                {
                  question: "What are the security measures in place?",
                  answer: "We follow top-tier crypto security practices including air-gapped cold storage, diversification across multiple wallets, periodic proof-of-reserves, and fully on-chain strategies."
                }

              ].map((faq, index) => (
                <div key={index} className="border border-white/10 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    className="w-full px-4 lg:px-6 py-3 lg:py-4 text-left bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-between"
                  >
                    <span className="text-white font-medium text-sm lg:text-base pr-2">{faq.question}</span>
                    {openFAQ === index ? (
                      <ChevronUp className="h-4 w-4 lg:h-5 lg:w-5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-4 w-4 lg:h-5 lg:w-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {openFAQ === index && (
                    <div className="px-4 lg:px-6 py-3 lg:py-4 bg-white/5 border-t border-white/10">
                      <p className="text-gray-300 leading-relaxed text-sm lg:text-base">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
                    {/* Mobile: Contact Section - Show only on mobile */}
          <div className="block lg:hidden bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Have any questions?</h3>
            <p className="text-xs text-gray-300 mb-4">
              {strategy.category === 'Vault' 
                ? 'Our investment advisors are here to help you maximize your vault strategy.'
                : 'Our portfolio specialists can help optimize your investment strategy.'
              }
            </p>
            <button 
              onClick={() => onContactAdvisor()}
              className="w-full bg-[#676DFF] hover:bg-[#5A60E8] text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center text-sm"
            >
              <Phone className="h-4 w-4 mr-2" />
              Contact Advisor
            </button>
            <div className="text-xs text-gray-400 text-center mt-4 space-y-1">
              <p>info@abnexus.com</p>
              <p>+91 84315 47416</p>
            </div>
          </div>
        </div>

        {/* Sidebar - Hidden on mobile, shown on desktop */}
        <div className="hidden lg:block space-y-6 lg:space-y-8">
          {/* Performance Metrics */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 lg:p-6">
            <h3 className="text-lg lg:text-xl font-semibold text-white mb-4 lg:mb-6">Current Performance</h3>
            <div className="space-y-4">
              <div className="text-center p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <p className="text-xs lg:text-sm text-gray-400 mb-1 font-medium whitespace-nowrap">APY (Since Inception)</p>
                <p className="text-2xl lg:text-3xl font-bold text-emerald-400">{strategy.apy}</p>
              </div>
              <div className="text-center p-4 bg-[#676DFF]/10 border border-[#676DFF]/20 rounded-lg">
                <p className="text-xs lg:text-sm text-gray-400 mb-1 font-medium">Total NAV</p>
                <p className="text-2xl lg:text-3xl font-bold text-[#676DFF]">{currentNAV}</p>
              </div>
            </div>
          </div>

          {/* Fee Information */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 lg:p-6">
            <h3 className="text-lg lg:text-xl font-semibold text-white mb-4 lg:mb-6">Fee Structure</h3>
            <div className="space-y-3 lg:space-y-4">
              <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 font-medium text-sm lg:text-base">Management Fee</span>
                <span className="font-bold text-white">1.5%</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 font-medium text-sm lg:text-base">Performance Fee</span>
                <span className="font-bold text-white">15%</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 font-medium text-sm lg:text-base">Withdrawal Fee</span>
                <span className="font-bold text-emerald-400">0%</span>
              </div>
            </div>
          </div>

          {/* Withdrawal Information */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 lg:p-6">
            <h3 className="text-lg lg:text-xl font-semibold text-white mb-4 lg:mb-6">Withdrawal Policy</h3>
            <div className="space-y-3 text-xs lg:text-sm text-gray-300">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                <p>Withdrawals processed within 3-7 days</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                <p>No lock-up periods or penalties</p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 lg:p-6">
            <h3 className="text-lg lg:text-xl font-semibold text-white mb-4">Have any questions?</h3>
            <p className="text-xs lg:text-sm text-gray-300 mb-4 lg:mb-6">
              Our investment advisors are here to help you maximize your vault strategy.
            </p>
            <button 
              onClick={() => onContactAdvisor()}
              className="w-full bg-[#676DFF] hover:bg-[#5A60E8] text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center text-sm lg:text-base"
            >
              <Phone className="h-4 w-4 mr-2" />
              Contact Advisor
            </button>
            <div className="text-xs text-gray-400 text-center mt-4 space-y-1">
              <p>info@abnexus.com</p>
              <p>+91 84315 47416</p>
            </div>
          </div>
        </div>
      </div>

{/* Modals */}
<KYCStatusModal 
  isOpen={showKYCModal}
  onClose={() => setShowKYCModal(false)}
  kycStatus={currentKycStatus}
  onNavigateToProfile={onNavigateToProfile}
/>

<StrategySelectionModal
  isOpen={showStrategySelectionModal}
  onClose={() => setShowStrategySelectionModal(false)}
  strategies={strategies}
  clickedStrategy={clickedStrategy}
  onProceedToInvestment={handleProceedToInvestment}
/>

<InvestmentModal 
  isOpen={showInvestmentModal}
  onClose={() => setShowInvestmentModal(false)}
  strategy={selectedStrategiesForInvestment.length > 0 ? selectedStrategiesForInvestment : selectedStrategyForInvestment}
  userStatus={userStatus}
  client={client}
/>
    </div>
  );
}
    
// PART 5: PORTFOLIO DETAILED VIEW - Mobile Layout Changes
// Replace the existing Portfolio section in your DetailedStrategyView function

if (strategy.category === 'Portfolio') {
  // Helper function to get portfolio-specific content
  const getPortfolioContent = (strategyId) => {
    switch (strategyId) {
      case 'BLUEC':
        return {
          objective: "Maximize exposure to BTC and ETH while maintaining portfolio stability through strategic USD and Gold allocations. The strategy aims to capture the growth potential of crypto's foundational assets while providing downside protection during market volatility.",
          selectionCriteria: "This basket holds BTC, ETH, and may hold USD/Gold to stabilize.",
          weightingCriteria: "Weightage is determined by BTC dominance trends, Overall market conditions. Profits are taken at key price levels, and rotated back in based on technical indicators to optimize risk-adjusted returns.",
          rebalancing: "Portfolios are rebalanced every quarter and at key price levels for individual assets."
        };
      case 'ALTC':
        return {
          objective: "Curated selection of altcoins from the top 50 by market cap, focusing on fundamental strength, technical performance, and long-term growth potential.",
          selectionCriteria: "Fundamentals: TVL, revenues, users, and growth metrics. Technicals: Strong price movement relative to the rest of the market. Sentiment: Social sentiment, buy and hold metrics, and long-term investor sentiment.",
          weightingCriteria: "Higher market cap tokens get more weightage. Tokens with high fundamental growth and price holding during downturns are given higher weightage. When mindshare reduces, token allocations are reduced. Profits are taken at key price levels, and rotated back in based on technical indicators to optimize risk-adjusted returns.",
          rebalancing: "Portfolios are rebalanced every quarter and at key price levels for individual assets. Poor performers are cycled out during rebalancing to maintain portfolio quality and performance."
        };
      case 'SMALLC':
        return {
          objective: "Curated selection of high potential for growth coins, focusing on mindshare capture, short-term growth metrics, and narrative bias.",
          selectionCriteria: "Fundamentals: TVL, revenues, users, and growth metrics. Technicals: Strong price movement relative to the rest of the market. Sentiment: Social sentiment, buy and hold metrics, and long-term investor sentiment.",
          weightingCriteria: "Tokens with high fundamental growth and price holding during downturns are given higher weightage. When mindshare reduces, token allocations are reduced. Profits are taken at key price levels, and rotated back in based on technical indicators to optimize risk-adjusted returns.",
          rebalancing: "Portfolios are rebalanced every quarter and at key price levels for individual assets. Poor performers are cycled out during rebalancing to maintain portfolio quality and performance."
        };
      default:
        return {
          objective: "Professional cryptocurrency portfolio management with systematic approach to risk and return optimization.",
          selectionCriteria: "Rigorous fundamental and technical analysis with focus on long-term value creation.",
          weightingCriteria: "Strategic allocation based on market dynamics and risk management principles.",
          rebalancing: "Regular portfolio optimization to maintain target allocations and performance objectives."
        };
    }
  };

  const portfolioContent = getPortfolioContent(strategy.strategyId);

  return (
    <div className="space-y-6 lg:space-y-8" style={{fontFamily: 'Lexend, sans-serif'}}>
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button 
          onClick={handleBackToStrategies}
          className="flex items-center text-gray-400 hover:text-white transition-colors font-medium"
        >
          <ArrowLeft className="h-4 w-4 lg:h-5 lg:w-5 mr-2 lg:mr-3" />
          <span className="text-sm lg:text-base">Back to Strategies</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6 lg:space-y-8">
          {/* Portfolio Name & Summary */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start mb-6 space-y-4 lg:space-y-0">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-4">
                  {getStrategyImage(strategy.id) ? (
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl overflow-hidden shadow-lg">
                      <img 
                        src={getStrategyImage(strategy.id)}
                        alt={strategy.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.parentNode.innerHTML = `
                            <div class="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                              <span class="text-white font-bold text-base lg:text-lg">P</span>
                            </div>
                          `;
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                      <span className="text-white font-bold text-base lg:text-lg">P</span>
                    </div>
                  )}
                  <h1 className="text-xl sm:text-2xl font-bold text-white">{strategy.name}</h1>
                </div>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  {(() => {
                    switch (strategy.name) {
                      case 'Nexus Core':
                        return 'Nexus Core anchors your portfolio with Bitcoin and Ethereum, the most enduring and liquid assets in crypto. With active stablecoin allocation for flexibility, it provides a resilient base designed to capture long-term value creation and sustainable growth.';
                      case 'Nexus Nova':
                        return 'Nexus Nova focuses on large-cap altcoins driving innovation in scalability, infrastructure, and DeFi ecosystems. By balancing growth exposure with stablecoin reserves, it allows investors to participate in the expansion of leading blockchain networks while managing risk.';
                      case 'Nexus Alpha':
                        return 'Nexus Alpha targets small and mid-cap altcoins with disruptive potential. Combining research-driven allocation with disciplined liquidity management, it aims to capture outsized opportunities in emerging narratives while navigating market volatility.';
                      default:
                        return 'Generate sustainable yield through diversified DeFi strategies while maintaining security and liquidity.';
                    }
                  })()}
                </p>
              </div>
              <div className="flex flex-col space-y-3 lg:ml-8">
                <button 
                  onClick={() => onInvestNow(strategy)}
                  className="bg-[#676DFF] hover:bg-[#5A60E8] text-white font-medium py-3 px-4 lg:px-6 rounded-lg transition-colors text-sm lg:text-base"
                >
                  Invest Now
                </button>
              </div>
            </div>
          </div>

          {/* Mobile: Portfolio Stats - Show only on mobile */}
          <div className="block lg:hidden bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Portfolio Overview</h3>
            <div className="space-y-4">
              <div className="text-center p-4 bg-[#676DFF]/10 border border-[#676DFF]/20 rounded-lg">
                <p className="text-xs text-gray-400 mb-1 font-medium">Total NAV</p>
                <p className="text-2xl font-bold text-[#676DFF]">{currentNAV}</p>
              </div>
              <div className="text-center p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <p className="text-xs text-gray-400 mb-1 font-medium">Total Investors</p>
                <p className="text-2xl font-bold text-emerald-400">{currentStrategyData.totalInvestors || strategy.investors}</p>
              </div>
            </div>
          </div>{/* Portfolio Strategy Details */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
            <div className="space-y-6 lg:space-y-8">
              {/* Strategy Objective */}
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">Strategy Objective</h3>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  {portfolioContent.objective}
                </p>
              </div>

              {/* Asset Selection Criteria */}
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">Asset Selection Criteria</h3>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  {portfolioContent.selectionCriteria}
                </p>
              </div>

              {/* Asset Weightage Criteria */}
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">Asset Weightage Criteria</h3>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  {portfolioContent.weightingCriteria}
                </p>
              </div>

              {/* Rebalancing */}
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">Rebalancing</h3>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  {portfolioContent.rebalancing}
                </p>
              </div>
            </div>
          </div>

          {/* NAV Chart */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">NAV Performance</h3>
            {navHistory[strategy.strategyId] ? (
              <NAVChart data={navHistory[strategy.strategyId]} />
            ) : (
              <div className="h-64 sm:h-80 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <TrendingUp className="h-12 w-12 lg:h-16 lg:w-16 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400 text-base lg:text-lg font-medium">No NAV data available</p>
                </div>
              </div>
            )}
          </div>{/* Asset Allocation */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">Asset Allocation</h3>
            <div className="space-y-3 lg:space-y-4">
              {(() => {
                const assets = strategyAssets.filter(asset => parseFloat(asset.allocation) > 0)
                  .sort((a, b) => parseFloat(b.allocation) - parseFloat(a.allocation));
                
                if (assets.length === 0) {
                  return (
                    <div className="text-center py-8 lg:py-12 text-gray-400">
                      <div className="w-12 h-12 lg:w-16 lg:h-16 bg-gray-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <div className="w-6 h-6 lg:w-8 lg:h-8 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                      <p className="text-sm lg:text-base">Loading asset allocation...</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3 lg:space-y-4">
                    {assets.map((asset, index) => {
                      const allocation = parseFloat(asset.allocation);
                      const color = asset.hex_code || getDefaultHexCode(asset.asset_id);
                      
                      return (
                        <div key={index} className="group">
                          {/* Mobile Card Layout */}
                          <div className="block sm:hidden">
                            <div className="p-3 bg-white/5 rounded-lg border border-white/10 hover:bg-white/8 transition-colors">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2 flex-1 min-w-0">
                                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }}></div>
                                  <span className="text-white font-medium text-sm truncate">
                                    {asset.asset_name || 'Unknown Asset'}
                                  </span>
                                </div>
                                <span className="text-[#676DFF] font-bold text-lg ml-2 flex-shrink-0">{allocation.toFixed(1)}%</span>
                              </div>
                              <div className="mt-2">
                                <div className="w-full bg-white/10 rounded-full h-2">
                                  <div 
                                    className="h-2 rounded-full transition-all duration-500 ease-out"
                                    style={{ 
                                      backgroundColor: color, 
                                      width: `${Math.min(allocation, 100)}%` 
                                    }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Desktop Row Layout */}
                          <div className="hidden sm:flex items-center justify-between p-3 lg:p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/8 transition-colors">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <div className="w-3 h-3 lg:w-4 lg:h-4 rounded-full flex-shrink-0" style={{ backgroundColor: color }}></div>
                              <div className="flex-1 min-w-0">
                                <span className="text-white font-medium text-sm lg:text-base block truncate">
                                  {asset.asset_name || 'Unknown Asset'}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-4 flex-shrink-0">
                              <div className="w-24 lg:w-32 bg-white/10 rounded-full h-2 lg:h-3">
                                <div 
                                  className="h-2 lg:h-3 rounded-full transition-all duration-500 ease-out"
                                  style={{ 
                                    backgroundColor: color, 
                                    width: `${Math.min(allocation, 100)}%` 
                                  }}
                                ></div>
                              </div>
                              <span className="text-gray-400 text-xs lg:text-sm font-medium w-16 text-right">{allocation.toFixed(1)}%</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Sector Breakdown */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">Sector Breakdown</h3>
            <div className="space-y-4 lg:space-y-6">
              {strategySectors.length > 0 ? (
                strategySectors
                  .filter(sector => sector.allocation > 0)
                  .sort((a, b) => b.allocation - a.allocation)
                  .map((sector, index) => {
                    const colors = ['text-purple-500', 'text-cyan-500', 'text-emerald-500', 'text-amber-500'];
                    const colorClass = colors[index % colors.length];
                    
                    return (
                      <div key={index} className="p-4 sm:p-6 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-white font-semibold text-base lg:text-lg">{sector.sector_name}</h4>
                          <span className={`${colorClass} font-bold text-lg lg:text-xl`}>{sector.allocation.toFixed(1)}%</span>
                        </div>
                        <p className="text-gray-400 text-xs lg:text-sm">
                          {sector.sector_description || `Investment allocation in ${sector.sector_name} sector.`}
                        </p>
                      </div>
                    );
                  })
              ) : (
                // Default sector breakdown if no data
                <>
                  {strategy.strategyId === 'BLUEC' && (
                    <>
                      <div className="p-4 sm:p-6 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-white font-semibold text-base lg:text-lg">Store of Value</h4>
                          <span className="text-orange-500 font-bold text-lg lg:text-xl">60%</span>
                        </div>
                        <p className="text-gray-400 text-xs lg:text-sm">Bitcoin allocation focused on long-term value preservation and digital gold properties.</p>
                      </div>
                      <div className="p-4 sm:p-6 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-white font-semibold text-base lg:text-lg">Smart Contract Platform</h4>
                          <span className="text-blue-500 font-bold text-lg lg:text-xl">40%</span>
                        </div>
                        <p className="text-gray-400 text-xs lg:text-sm">Ethereum exposure for ecosystem growth and smart contract innovation.</p>
                      </div>
                    </>
                  )}
                  {strategy.strategyId === 'ALTC' && (
                    <>
                      <div className="p-4 sm:p-6 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-white font-semibold text-base lg:text-lg">Layer 1 Blockchains</h4>
                          <span className="text-purple-500 font-bold text-lg lg:text-xl">65%</span>
                        </div>
                        <p className="text-gray-400 text-xs lg:text-sm">Alternative blockchain platforms with strong ecosystems and development activity.</p>
                      </div>
                      <div className="p-4 sm:p-6 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-white font-semibold text-base lg:text-lg">Infrastructure</h4>
                          <span className="text-cyan-500 font-bold text-lg lg:text-xl">35%</span>
                        </div>
                        <p className="text-gray-400 text-xs lg:text-sm">Oracle networks, scaling solutions, and blockchain infrastructure projects.</p>
                      </div>
                    </>
                  )}
                  {strategy.strategyId === 'SMALLC' && (
                    <>
                      <div className="p-4 sm:p-6 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-white font-semibold text-base lg:text-lg">DeFi Innovation</h4>
                          <span className="text-emerald-500 font-bold text-lg lg:text-xl">35%</span>
                        </div>
                        <p className="text-gray-400 text-xs lg:text-sm">Next-generation DeFi protocols and financial innovation projects.</p>
                      </div>
                      <div className="p-4 sm:p-6 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-white font-semibold text-base lg:text-lg">Gaming & Entertainment</h4>
                          <span className="text-purple-500 font-bold text-lg lg:text-xl">35%</span>
                        </div>
                        <p className="text-gray-400 text-xs lg:text-sm">GameFi, NFTs, and entertainment-focused blockchain projects.</p>
                      </div>
                      <div className="p-4 sm:p-6 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-white font-semibold text-base lg:text-lg">Emerging Trends</h4>
                          <span className="text-amber-500 font-bold text-lg lg:text-xl">30%</span>
                        </div>
                        <p className="text-gray-400 text-xs lg:text-sm">New sector opportunities and breakthrough technologies in blockchain.</p>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>{/* Mobile: Fee Information - Show only on mobile */}
          <div className="block lg:hidden bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Fee Structure</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 font-medium text-sm">Management Fee</span>
                <span className="font-bold text-white">2%</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 font-medium text-sm">Performance Fee</span>
                <span className="font-bold text-white">20%</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 font-medium text-sm">Withdrawal Fee</span>
                <span className="font-bold text-emerald-400">0%</span>
              </div>
            </div>
          </div>

          {/* Mobile: Withdrawal Information - Show only on mobile */}
          <div className="block lg:hidden bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Withdrawal Policy</h3>
            <div className="space-y-3 text-xs text-gray-300">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                <p>Withdrawals processed within 3-7 days</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                <p>No lock-up periods or penalties</p>
              </div>
            </div>
          </div>

          {/* Portfolio FAQ */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">Frequently Asked Questions</h3>
            <div className="space-y-3 lg:space-y-4">
              {[
                {
                  question: "How are assets selected for the portfolio?",
                  answer: ` We select ${strategy.name} assets based on price action through technical and trend analysis, value proposition by evaluating fundamentals and project strength, and market sentiment by assessing overall community and investor outlook.`
                },
                {
                  question: "How often is the portfolio rebalanced?",
                  answer: "We rebalance every quarter.Additionally, we rebalance when there’s a strong price move in either direction — taking profits or dollar-cost averaging (DCA) into positions."
                },
                {
                  question: "What is the minimum investment amount?",
                  answer: "The minimum investment for portfolio strategies is $1,000. This ensures sufficient diversification and cost-effective management for all investors."
                },
                {
                  question: "Can I customize my portfolio allocation?",
                  answer: "You can choose how much to invest in each portfolio. However, the asset selection and weightages within each portfolio are managed by our investment team. For investments over $50,000, we offer fully curated portfolios — get in touch."
                },
                {
                  question: "How do you manage portfolio risk?",
                  answer: "We focus on high-value projects with strong fundamentals and allocate based on market cap, giving smaller allocations to smaller caps to reduce overall risk."
                },
                {
                  question: "What happens during market downturns?",
                  answer: "We take profits and DCA as markets move, smoothing volatility. A healthy stablecoin allocation allows us to re-enter strategically, historically outperforming the market by 50–75% during downturns."
                }
              ].map((faq, index) => (
                <div key={index} className="border border-white/10 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    className="w-full px-4 lg:px-6 py-3 lg:py-4 text-left bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-between"
                  >
                    <span className="text-white font-medium text-sm lg:text-base pr-2">{faq.question}</span>
                    {openFAQ === index ? (
                      <ChevronUp className="h-4 w-4 lg:h-5 lg:w-5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-4 w-4 lg:h-5 lg:w-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {openFAQ === index && (
                    <div className="px-4 lg:px-6 py-3 lg:py-4 bg-white/5 border-t border-white/10">
                      <p className="text-gray-300 leading-relaxed text-sm lg:text-base">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
                    {/* Mobile: Contact Section - Show only on mobile */}
          <div className="block lg:hidden bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Have any questions?</h3>
            <p className="text-xs text-gray-300 mb-4">
              {strategy.category === 'Vault' 
                ? 'Our investment advisors are here to help you maximize your vault strategy.'
                : 'Our portfolio specialists can help optimize your investment strategy.'
              }
            </p>
            <button 
              onClick={() => onContactAdvisor()}
              className="w-full bg-[#676DFF] hover:bg-[#5A60E8] text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center text-sm"
            >
              <Phone className="h-4 w-4 mr-2" />
              Contact Advisor
            </button>
            <div className="text-xs text-gray-400 text-center mt-4 space-y-1">
              <p>info@abnexus.com</p>
              <p>+91 84315 47416</p>
            </div>
          </div>
        </div>

        {/* Sidebar - Hidden on mobile, shown on desktop */}
        <div className="hidden lg:block space-y-6 lg:space-y-8">
          {/* Portfolio Stats */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 lg:p-6">
            <h3 className="text-lg lg:text-xl font-semibold text-white mb-4 lg:mb-6">Portfolio Overview</h3>
            <div className="space-y-4">
              <div className="text-center p-4 bg-[#676DFF]/10 border border-[#676DFF]/20 rounded-lg">
                <p className="text-xs lg:text-sm text-gray-400 mb-1 font-medium">Total NAV</p>
                <p className="text-2xl lg:text-3xl font-bold text-[#676DFF]">{currentNAV}</p>
              </div>
              <div className="text-center p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <p className="text-xs lg:text-sm text-gray-400 mb-1 font-medium">Total Investors</p>
                <p className="text-2xl lg:text-3xl font-bold text-emerald-400">{currentStrategyData.totalInvestors || strategy.investors}</p>
              </div>
            </div>
          </div>

          {/* Fee Information */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 lg:p-6">
            <h3 className="text-lg lg:text-xl font-semibold text-white mb-4 lg:mb-6">Fee Structure</h3>
            <div className="space-y-3 lg:space-y-4">
              <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 font-medium text-sm lg:text-base">Management Fee</span>
                <span className="font-bold text-white">2%</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 font-medium text-sm lg:text-base">Performance Fee</span>
                <span className="font-bold text-white">20%</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 font-medium text-sm lg:text-base">Withdrawal Fee</span>
                <span className="font-bold text-emerald-400">0%</span>
              </div>
            </div>
          </div>

          {/* Withdrawal Information */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 lg:p-6">
            <h3 className="text-lg lg:text-xl font-semibold text-white mb-4 lg:mb-6">Withdrawal Policy</h3>
            <div className="space-y-3 text-xs lg:text-sm text-gray-300">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                <p>Withdrawals processed within 3-7 days</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                <p>No lock-up periods or penalties</p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 lg:p-6">
            <h3 className="text-lg lg:text-xl font-semibold text-white mb-4">Have any questions?</h3>
            <p className="text-xs lg:text-sm text-gray-300 mb-4 lg:mb-6">
              Our portfolio specialists can help optimize your investment strategy.
            </p>
            <button 
              onClick={() => onContactAdvisor()}
              className="w-full bg-[#676DFF] hover:bg-[#5A60E8] text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center text-sm lg:text-base"
            >
              <Phone className="h-4 w-4 mr-2" />
              Contact Advisor
            </button>
            <div className="text-xs text-gray-400 text-center mt-4 space-y-1">
              <p>info@abnexus.com</p>
              <p>+91 84315 47416</p>
            </div>
          </div>
        </div>
      </div>

{/* Modals */}
<KYCStatusModal 
  isOpen={showKYCModal}
  onClose={() => setShowKYCModal(false)}
  kycStatus={currentKycStatus}
  onNavigateToProfile={onNavigateToProfile}
/>

<StrategySelectionModal
  isOpen={showStrategySelectionModal}
  onClose={() => setShowStrategySelectionModal(false)}
  strategies={strategies}
  clickedStrategy={clickedStrategy}
  onProceedToInvestment={handleProceedToInvestment}
/>

<InvestmentModal 
  isOpen={showInvestmentModal}
  onClose={() => setShowInvestmentModal(false)}
  strategy={selectedStrategiesForInvestment.length > 0 ? selectedStrategiesForInvestment : selectedStrategyForInvestment}
  userStatus={userStatus}
  client={client}
/>
    </div>
  );
}
    // SIP DETAILED VIEW - Enhanced responsive design
    if (strategy.category === 'SIP') {
      const results = calculateSIPReturns();
      
      return (
        <div className="space-y-6 lg:space-y-8" style={{fontFamily: 'Lexend, sans-serif'}}>
          {/* Header */}
          <div className="flex items-center space-x-4">
            <button 
              onClick={handleBackToStrategies}
              className="flex items-center text-gray-400 hover:text-white transition-colors font-medium"
            >
              <ArrowLeft className="h-4 w-4 lg:h-5 lg:w-5 mr-2 lg:mr-3" />
              <span className="text-sm lg:text-base">Back to Strategies</span>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6 lg:space-y-8">
              {/* SIP Overview */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start mb-6 space-y-4 lg:space-y-0">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-4">
                      {getStrategyImage(strategy.id) ? (
                        <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl overflow-hidden shadow-lg">
                          <img 
                            src={getStrategyImage(strategy.id)}
                            alt={strategy.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.parentNode.innerHTML = `
                                <div class="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                                  <span class="text-white font-bold text-base lg:text-lg">S</span>
                                </div>
                              `;
                            }}
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                          <span className="text-white font-bold text-base lg:text-lg">S</span>
                        </div>
                      )}
                      <h1 className="text-xl sm:text-2xl font-bold text-white">Systematic Investment Plan</h1>
                    </div>
                    <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                      Our Systematic Investment Plan (SIP) allows you to invest a fixed amount regularly into your chosen cryptocurrency strategies. This disciplined approach helps reduce market volatility impact through dollar-cost averaging while building wealth systematically over time.
                    </p>
                  </div>
                  <div className="flex flex-col space-y-3 lg:ml-8">
                    <button 
                      onClick={() => onInvestNow(strategy)}
                      className="bg-[#676DFF] hover:bg-[#5A60E8] text-white font-medium py-3 px-4 lg:px-6 rounded-lg transition-colors text-sm lg:text-base"
                    >
                      Start SIP
                    </button>
                  </div>
                </div>
              </div>

              {/* Assets We Offer */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">Assets Available</h3>
                <div className="space-y-3 lg:space-y-4">
                  {/* Bitcoin - Always Visible */}
                  <div className="flex items-center justify-between p-3 lg:p-4 bg-white/5 rounded-lg border border-white/10">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span className="text-white font-medium text-sm lg:text-base">Bitcoin (BTC)</span>
                    </div>
                    
                  </div>
                  
                  {/* Ethereum - Always Visible */}
                  <div className="flex items-center justify-between p-3 lg:p-4 bg-white/5 rounded-lg border border-white/10">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="text-white font-medium text-sm lg:text-base">Ethereum (ETH)</span>
                    </div>

                  </div>

                  {/* Professional Expandable Button */}
                  <button
                    onClick={() => setSipFAQOpen(sipFAQOpen === 'assets' ? null : 'assets')}
                    className="group w-full flex items-center justify-between p-3 lg:p-4 bg-gradient-to-r from-[#676DFF]/10 to-[#5A60E8]/5 hover:from-[#676DFF]/20 hover:to-[#5A60E8]/10 rounded-xl border border-[#676DFF]/20 hover:border-[#676DFF]/40 transition-all duration-300 hover:shadow-lg hover:shadow-[#676DFF]/10"
                  >
                    <div className="flex items-center space-x-3 lg:space-x-4">
                      <div className="relative">
                        <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-r from-[#676DFF] to-[#5A60E8] rounded-lg flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:shadow-[#676DFF]/25 transition-all duration-300 group-hover:scale-105">
                          {sipFAQOpen === 'assets' ? (
                            <ChevronUp className="h-4 w-4 lg:h-5 lg:w-5 text-white transition-transform duration-200" />
                          ) : (
                            <span className="text-white font-medium text-lg lg:text-xl transition-transform duration-200 group-hover:scale-110">+</span>
                          )}
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-r from-[#676DFF] to-[#5A60E8] rounded-lg opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300"></div>
                      </div>
                      <div className="text-left">
                        <span className="text-white font-semibold text-sm lg:text-base block">
                          {sipFAQOpen === 'assets' ? 'Hide Additional Assets' : 'View More Assets'}
                        </span>
                        <span className="text-gray-400 text-xs lg:text-sm">
                          {sipFAQOpen === 'assets' ? 'Click to collapse' : '3 more asset types available'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-[#676DFF] text-xs lg:text-sm font-medium opacity-70 group-hover:opacity-100 transition-opacity">
                        {sipFAQOpen === 'assets' ? 'Collapse' : 'Expand'}
                      </span>
                      <div className="w-5 h-5 lg:w-6 lg:h-6 rounded-full bg-[#676DFF]/20 flex items-center justify-center group-hover:bg-[#676DFF]/30 transition-colors">
                        {sipFAQOpen === 'assets' ? (
                          <ChevronUp className="h-3 w-3 lg:h-4 lg:w-4 text-[#676DFF]" />
                        ) : (
                          <ChevronDown className="h-3 w-3 lg:h-4 lg:w-4 text-[#676DFF]" />
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Collapsible Additional Assets */}
                  {sipFAQOpen === 'assets' && (
                    <div className="space-y-3 animate-in slide-in-from-top-2 duration-300">
                      <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent my-4"></div>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 lg:p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/8 transition-colors">
                        <div className="flex items-center space-x-3 mb-2 sm:mb-0">
                          <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                          <span className="text-white font-medium text-sm lg:text-base">Aave</span>
                        </div>

                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 lg:p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/8 transition-colors">
                        <div className="flex items-center space-x-3 mb-2 sm:mb-0">
                          <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                          <span className="text-white font-medium text-sm lg:text-base">Uniswap</span>
                        </div>

                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 lg:p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/8 transition-colors">
                        <div className="flex items-center space-x-3 mb-2 sm:mb-0">
                          <div className="w-3 h-3 bg-pink-500 rounded-full"></div>
                          <span className="text-white font-medium text-sm lg:text-base">Chainlink</span>
                        </div>

                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* What are SIPs */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">What are SIPs?</h3>
                <div className="space-y-4 lg:space-y-6">
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                    A Systematic Investment Plan (SIP) is a disciplined investment approach where you invest a fixed amount at regular intervals. Instead of trying to time the market, SIP helps you build wealth gradually while averaging out market fluctuations.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                    <div className="text-center p-4 lg:p-6 bg-white/5 rounded-lg border border-white/10">
                      <div className="w-10 h-10 lg:w-12 lg:h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <TrendingUp className="h-5 w-5 lg:h-6 lg:w-6 text-emerald-400" />
                      </div>
                      <h4 className="text-white font-semibold mb-2 text-sm lg:text-base">Dollar Cost Averaging</h4>
                      <p className="text-gray-400 text-xs lg:text-sm">Reduces impact of market volatility by spreading investments over time</p>
                    </div>
                    <div className="text-center p-4 lg:p-6 bg-white/5 rounded-lg border border-white/10">
                      <div className="w-10 h-10 lg:w-12 lg:h-12 bg-[#676DFF]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <DollarSign className="h-5 w-5 lg:h-6 lg:w-6 text-[#676DFF]" />
                      </div>
                      <h4 className="text-white font-semibold mb-2 text-sm lg:text-base">Disciplined Investing</h4>
                      <p className="text-gray-400 text-xs lg:text-sm">Automated approach removes emotion from investment decisions</p>
                    </div>
                    <div className="text-center p-4 lg:p-6 bg-white/5 rounded-lg border border-white/10">
                      <div className="w-10 h-10 lg:w-12 lg:h-12 bg-amber-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <Users className="h-5 w-5 lg:h-6 lg:w-6 text-amber-400" />
                      </div>
                      <h4 className="text-white font-semibold mb-2 text-sm lg:text-base">Flexible & Convenient</h4>
                      <p className="text-gray-400 text-xs lg:text-sm">Start small, modify anytime, and invest across multiple strategies</p>
                    </div>
                  </div>
                </div>
              </div>{/* How It Works */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">How AB Nexus SIP Works</h3>
                <div className="space-y-6 lg:space-y-8">
                  <div className="flex space-x-4 lg:space-x-6">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-[#676DFF] flex items-center justify-center text-white font-bold text-base lg:text-lg flex-shrink-0">
                      1
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white text-base lg:text-lg mb-2">Choose Your Strategy</h4>
                      <p className="text-gray-300 text-sm lg:text-base">Select from our range of vault and portfolio strategies based on your risk profile and investment goals. You can invest in multiple strategies simultaneously.</p>
                    </div>
                  </div>
                  <div className="flex space-x-4 lg:space-x-6">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-[#676DFF] flex items-center justify-center text-white font-bold text-base lg:text-lg flex-shrink-0">
                      2
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white text-base lg:text-lg mb-2">Set Amount & Frequency</h4>
                      <p className="text-gray-300 text-sm lg:text-base">Decide how much you want to invest (minimum $50) and how often - monthly, bi-weekly, or weekly. Our system will automatically execute your investments.</p>
                    </div>
                  </div>
                  <div className="flex space-x-4 lg:space-x-6">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-[#676DFF] flex items-center justify-center text-white font-bold text-base lg:text-lg flex-shrink-0">
                      3
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white text-base lg:text-lg mb-2">Automate Payments</h4>
                      <p className="text-gray-300 text-sm lg:text-base">Link your bank account or set up automatic transfers. Your investments are executed on your chosen date each period at prevailing market prices.</p>
                    </div>
                  </div>
                  <div className="flex space-x-4 lg:space-x-6">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-[#676DFF] flex items-center justify-center text-white font-bold text-base lg:text-lg flex-shrink-0">
                      4
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white text-base lg:text-lg mb-2">Track & Optimize</h4>
                      <p className="text-gray-300 text-sm lg:text-base">Monitor your progress through our dashboard. View your dollar-cost average, total returns, and adjust your strategy as your goals evolve.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced SIP FAQ */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 lg:mb-6">Frequently Asked Questions</h3>
                <div className="space-y-3 lg:space-y-4">
                  {[
                    {
                      question: "What is the minimum SIP amount?",
                      answer: "The minimum SIP amount is $50 per month . This makes systematic investing accessible to a wide range of investors."
                    },
                    {
                      question: "Can I change my SIP amount or frequency?",
                      answer: "Yes — you can request changes anytime by contacting us. Updates are applied within 7 working days."
                    },
                    {
                      question: "When are SIP investments executed?",
                      answer: "All SIPs are executed automatically through our in-house infrastructure — Stackit."
                    },
                    {
                      question: "Can I invest in multiple strategies through SIP?",
                      answer: "Yes. Currently, we offer SIPs in BTC and ETH. For investments exceeding $5,000 per month, we also offer curated SIPs for other assets. Contact us for more details."
                    },
                    {
                      question: "What if I miss a SIP payment?",
                      answer: "Missing a payment simply skips that month’s investment — your SIP continues as normal from the next scheduled date."
                    },
                    {
                      question: "How is dollar-cost averaging beneficial?",
                      answer: "It removes emotions from investing, smooths volatility, requires minimal effort, and is ideal for consistent long-term growth."
                    }
                  ].map((faq, index) => (
                    <div key={index} className="border border-white/10 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setSipFAQOpen(sipFAQOpen === index ? null : index)}
                        className="w-full px-4 lg:px-6 py-3 lg:py-4 text-left bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-between"
                      >
                        <span className="text-white font-medium text-sm lg:text-base pr-2">{faq.question}</span>
                        {sipFAQOpen === index ? (
                          <ChevronUp className="h-4 w-4 lg:h-5 lg:w-5 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="h-4 w-4 lg:h-5 lg:w-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      {sipFAQOpen === index && (
                        <div className="px-4 lg:px-6 py-3 lg:py-4 bg-white/5 border-t border-white/10">
                          <p className="text-gray-300 leading-relaxed text-sm lg:text-base">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6 lg:space-y-8">
              {/* SIP Overview */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 lg:p-6">
                <h3 className="text-lg lg:text-xl font-semibold text-white mb-4 lg:mb-6">Current SIP Stats</h3>
                <div className="space-y-4">
                  <div className="text-center p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                    <p className="text-xs lg:text-sm text-gray-400 mb-1 font-medium">MINIMUM INVESTMENT</p>
                    <p className="text-xl lg:text-2xl font-bold text-emerald-400">{strategy.minInvestment}</p>
                  </div>
                  <div className="text-center p-4 bg-[#676DFF]/10 border border-[#676DFF]/20 rounded-lg">
                    <p className="text-xs lg:text-sm text-gray-400 mb-1 font-medium">ACTIVE PLANS</p>
                    <p className="text-xl lg:text-2xl font-bold text-[#676DFF]">{sipStats.activePlans || strategy.totalPlans}</p>
                  </div>

                </div>
              </div>

{/* SIP Calculator */}
<div className="bg-gradient-to-br from-[#676DFF]/10 to-[#5A60E8]/10 border border-[#676DFF]/20 rounded-xl p-4 lg:p-6">
  <div className="flex items-center mb-4 lg:mb-6">
    <div className="w-8 h-8 lg:w-10 lg:h-10 bg-[#676DFF]/20 rounded-lg flex items-center justify-center mr-3">
      <DollarSign className="h-4 w-4 lg:h-5 lg:w-5 text-[#676DFF]" />
    </div>
    <h3 className="text-lg lg:text-xl font-semibold text-white">SIP Calculator</h3>
  </div>
  
  <div className="space-y-4">
    <div>
      <label className="block text-xs lg:text-sm font-medium text-gray-300 mb-2">Monthly Amount ($)</label>
      <input
        type="number"
        value={sipAmount}
        onChange={(e) => setSipAmount(Number(e.target.value))}
        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-[#676DFF] focus:outline-none text-sm lg:text-base"
        min="100"
      />
    </div>
    
    <div>
      <label className="block text-xs lg:text-sm font-medium text-gray-300 mb-2">Duration (Months)</label>
      <input
        type="number"
        value={sipDuration}
        onChange={(e) => setSipDuration(Number(e.target.value))}
        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-[#676DFF] focus:outline-none text-sm lg:text-base"
        min="1"
      />
    </div>
    
    <div className="mt-4 lg:mt-6 p-4 bg-white/10 rounded-lg border border-white/20">
      <div className="text-center space-y-3">
        <div>
          <p className="text-xs text-gray-400 mb-1">Projected Value*</p>
          <p className="text-base lg:text-lg font-bold text-emerald-400">${Number(results.futureValue).toLocaleString()}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Projected Returns*</p>
          <p className="text-base lg:text-lg font-bold text-[#676DFF]">${Number(results.returns).toLocaleString()}</p>
        </div>
      </div>
      <p className="text-xs text-gray-500 text-center mt-3">*Assumes 35% annual returns (BTC historical average)</p>
    </div>
  </div>
</div>

              {/* Fee Structure */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 lg:p-6">
                <h3 className="text-lg lg:text-xl font-semibold text-white mb-4 lg:mb-6">Fee Structure</h3>
                <div className="space-y-3 lg:space-y-4">
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400 font-medium text-sm lg:text-base">Management Fee</span>
                    <span className="font-bold text-white">1%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400 font-medium text-sm lg:text-base">Plan Creation Fee</span>
                    <span className="font-bold text-emerald-400">$1</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400 font-medium text-sm lg:text-base">SIP Processing Fee</span>
                    <span className="font-bold text-emerald-400">0.5%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-400 font-medium text-sm lg:text-base">Withdrawal Fee</span>
                    <span className="font-bold text-emerald-400">0%</span>
                  </div>
                </div>
              </div>

              {/* Withdrawal Information */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 lg:p-6">
                <h3 className="text-lg lg:text-xl font-semibold text-white mb-4 lg:mb-6">Withdrawal Policy</h3>
                <div className="space-y-3 text-xs lg:text-sm text-gray-300">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p>Withdrawals processed within 3-7 days</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p>No lock-up periods or penalties</p>
                  </div>
                  {/* <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                    <p>24/7 Support Available</p>
                  </div> */}
                </div>
              </div>

              {/* Contact Section */}
{/* Contact Section */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 lg:p-6">
                <h3 className="text-lg lg:text-xl font-semibold text-white mb-4">Have any questions?</h3>
                <p className="text-xs lg:text-sm text-gray-300 mb-4 lg:mb-6">
                  Our SIP specialists can help you create the perfect investment plan.
                </p>
                <button 
                  onClick={() => onContactAdvisor()}
                  className="w-full bg-[#676DFF] hover:bg-[#5A60E8] text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center text-sm lg:text-base"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Contact Advisor
                </button>
                <div className="text-xs text-gray-400 text-center mt-4 space-y-1">
                  <p>info@abnexus.com</p>
                  <p>+91 84315 47416</p>
                </div>
              </div>
            </div>
          </div>

{/* Modals */}
<KYCStatusModal 
  isOpen={showKYCModal}
  onClose={() => setShowKYCModal(false)}
  kycStatus={currentKycStatus}
  onNavigateToProfile={onNavigateToProfile}
/>

<StrategySelectionModal
  isOpen={showStrategySelectionModal}
  onClose={() => setShowStrategySelectionModal(false)}
  strategies={strategies}
  clickedStrategy={clickedStrategy}
  onProceedToInvestment={handleProceedToInvestment}
/>

<InvestmentModal 
  isOpen={showInvestmentModal}
  onClose={() => setShowInvestmentModal(false)}
  strategy={selectedStrategiesForInvestment.length > 0 ? selectedStrategiesForInvestment : selectedStrategyForInvestment}
  userStatus={userStatus}
  client={client}
/>
        </div>
      );
    }

    return null;
  };
  // Main render logic
if (selectedStrategy) {
  return (
    <DetailedStrategyView 
      strategy={selectedStrategy} 
      onInvestNow={handleInvestNow}
      onContactAdvisor={handleContactAdvisor}
      showKYCModal={showKYCModal}
      setShowKYCModal={setShowKYCModal}
      showStrategySelectionModal={showStrategySelectionModal}
      setShowStrategySelectionModal={setShowStrategySelectionModal}
      showInvestmentModal={showInvestmentModal}
      setShowInvestmentModal={setShowInvestmentModal}
      currentKycStatus={currentKycStatus}
      setCurrentKycStatus={setCurrentKycStatus}
    />
  );
}
  // Loading state
  if (loading) {
    return (
      <div className="bg-[#0A0A0A] min-h-screen text-white py-6 lg:py-8 flex items-center justify-center" style={{fontFamily: 'Lexend, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'}}>
        <div className="text-center">
          <div className="w-12 h-12 lg:w-16 lg:h-16 border-4 border-[#676DFF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400 text-base lg:text-lg">Loading strategies...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-[#0A0A0A] min-h-screen text-white py-6 lg:py-8 px-4" style={{fontFamily: 'Lexend, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'}}>
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
            <p className="text-red-400 text-base lg:text-lg mb-4">Error loading strategies: {error}</p>
            <button 
              onClick={fetchStrategyData}
              className="bg-[#676DFF] hover:bg-[#5A60E8] text-white font-medium py-2 px-6 rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
      
    );
  }

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white py-6 lg:py-8 px-4 lg:px-0" style={{fontFamily: 'Lexend, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'}}>
      <style jsx>{`
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
      
      <div className="max-w-6xl mx-auto px-2 lg:px-6">
        <div className="mb-8 lg:mb-10">
          <p className="text-gray-400 text-base lg:text-lg leading-relaxed max-w-3xl">
            Discover our comprehensive range of investment strategies designed to meet different risk profiles and return objectives.
          </p>
        </div>

        {/* Strategy Categories */}
        <div className="space-y-8 lg:space-y-12">
          {/* Vaults Section */}
          {strategies.filter(s => s && s.category === 'Vault').length > 0 && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">VAULTS</h2>
                <span className="px-3 py-1.5 bg-[#676DFF]/15 text-[#676DFF] border border-[#676DFF]/30 rounded-lg text-sm font-semibold inline-block">
                  {strategies.filter(s => s && s.category === 'Vault').length} Available
                </span>
              </div>
              <div className="w-full h-px bg-gradient-to-r from-[#676DFF]/50 via-[#676DFF]/20 to-transparent mb-6"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
                {strategies.filter(s => s && s.category === 'Vault').map(strategy => (
                  <StrategyCard key={strategy.id} strategy={strategy} />
                ))}
              </div>
            </div>
          )}

          {/* Portfolios Section */}
          {strategies.filter(s => s && s.category === 'Portfolio').length > 0 && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">PORTFOLIOS</h2>
                <span className="px-3 py-1.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-lg text-sm font-semibold inline-block">
                  {strategies.filter(s => s && s.category === 'Portfolio').length} Available
                </span>
              </div>
              <div className="w-full h-px bg-gradient-to-r from-emerald-500/50 via-emerald-500/20 to-transparent mb-6"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
                {strategies.filter(s => s && s.category === 'Portfolio').map(strategy => (
                  <StrategyCard key={strategy.id} strategy={strategy} />
                ))}
              </div>
            </div>
          )}

          {/* SIP Section */}
          {strategies.filter(s => s && s.category === 'SIP').length > 0 && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-4">
                <h2 className="text-base sm:text-xl lg:text-2xl font-bold text-white tracking-tight">SYSTEMATIC INVESTMENT PLANS</h2>
                <span className="px-3 py-1.5 bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded-lg text-sm font-semibold inline-block">
                  {strategies.filter(s => s && s.category === 'SIP').length} Available
                </span>
              </div>
              <div className="w-full h-px bg-gradient-to-r from-amber-500/50 via-amber-500/20 to-transparent mb-6"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5">
                {strategies.filter(s => s && s.category === 'SIP').map(strategy => (
                  <StrategyCard key={strategy.id} strategy={strategy} />
                ))}
              </div>
            </div>
          )}

          {/* No Strategies Available */}
          {strategies.length === 0 && !loading && (
            <div className="text-center py-8 lg:py-12">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 lg:p-12">
                <Info className="h-12 w-12 lg:h-16 lg:w-16 text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg lg:text-xl font-semibold text-white mb-2">No Strategies Available</h3>
                <p className="text-gray-400">Strategies are currently being updated. Please check back later.</p>
              </div>
            </div>
          )}

          {/* FAQ Section */}
          <div className="mt-12 lg:mt-16">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6 lg:p-8">
              <div className="text-center mb-6 lg:mb-8">
                <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">Frequently Asked Questions</h2>
                <p className="text-gray-400 text-base lg:text-lg">
                  Find answers to common questions about our investment strategies and services.
                </p>
              </div>
              
              <div className="space-y-3 lg:space-y-4 max-w-4xl mx-auto">
                {faqData.map((faq, index) => (
                  <div key={index} className="border border-white/10 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                      className="w-full px-4 lg:px-6 py-3 lg:py-4 text-left bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-between"
                    >
                      <span className="text-white font-medium text-sm lg:text-base pr-2">{faq.question}</span>
                      {openFAQ === index ? (
                        <ChevronUp className="h-4 w-4 lg:h-5 lg:w-5 text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="h-4 w-4 lg:h-5 lg:w-5 text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    {openFAQ === index && (
                      <div className="px-4 lg:px-6 py-3 lg:py-4 bg-white/5 border-t border-white/10">
                        <p className="text-gray-300 leading-relaxed text-sm lg:text-base">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Data Attribution */}
          <div className="mt-6 lg:mt-8 text-center">
            <p className="text-xs text-gray-500">
              Performance data updated in real-time • NAV calculated daily • {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

{/* Modals */}
<KYCStatusModal 
  isOpen={showKYCModal}
  onClose={() => setShowKYCModal(false)}
  kycStatus={currentKycStatus}
  onNavigateToProfile={onNavigateToProfile}
/>

<StrategySelectionModal
  isOpen={showStrategySelectionModal}
  onClose={() => setShowStrategySelectionModal(false)}
  strategies={strategies}
  clickedStrategy={clickedStrategy}
  onProceedToInvestment={handleProceedToInvestment}
/>

<InvestmentModal 
  isOpen={showInvestmentModal}
  onClose={() => setShowInvestmentModal(false)}
  strategy={selectedStrategiesForInvestment.length > 0 ? selectedStrategiesForInvestment : selectedStrategyForInvestment}
  userStatus={userStatus}
  client={client}
/>
    </div>
  );
};

export default StrategiesSection;
